import axiosService from '../common/axiosService'
import CONFIG from '@constant/config'
import {
  DeleteAngusMasterFileRequest,
  DeleteAngusMasterFileResponse,
  DeleteImpakMasterFileRequest,
  DeleteImpakMasterFileResponse,
  GetBeMigrationStatusRequest,
  GetImportUploadDependenciesRequest,
  GetImportUploadDependenciesResponse,
  ListAngusMasterFilesRequest,
  ListAngusMasterFilesResponse,
  ListBeMigrationRunsRequest,
  ListBeMigrationRunsResponse,
  ListImpakMasterFilesRequest,
  ListImpakMasterFilesResponse,
  ResumeBeStepRequest,
  ResumeBeStepResponse,
  RunStatus,
  StartAngusMigrationRequest,
  StartAngusMigrationResponse,
  StartBeMigrationRequest,
  StartBeMigrationResponse,
  StartImpakMigrationRequest,
  StartImpakMigrationResponse,
  UploadAngusMasterFileRequest,
  UploadAngusMasterFileResponse,
  UploadImpakMasterFileRequest,
  UploadImpakMasterFileResponse,
} from '../types/migration'
import { GetEmailLogRequest } from '../types/emailLog'
import { TenantContactPicklistType, TenantContactPicklistsITF } from '../types/group'
// @ts-ignore
axiosService.setBaseUrl(CONFIG.apiHost)

const handleGetRequest = <T>(url: string, data?: any) => {
  return axiosService.get<T>(`/csm/${url}`, data)
}

const handlePostRequest = <T>(url: string, data: any) => {
  return axiosService.post<T>(`/csm/${url}`, data)
}

export const getQRBackgroundImage = (data: any) => {
  return {
    bId: data.bId,
    imageUrl: 'https://activate-dev-env.s3.us-east-2.amazonaws.com/group/c67c8611-9e4e-11ec-a185-019d2783e090/z_2.png',
  }
}

export const getAnnouncement = (data: any) => {
  return axiosService.get('/csm/Announcement/GetAnnouncements', data)
}

export const getCountFunctionalityScansByGroupId = (data: any) => {
  return axiosService.get('/csm/QrCode/CountFunctionalityScansByGroupId', data)
}
export const getScanStats = <T>(data?: any) => handleGetRequest<T>('QrCode/GetScanStats', data)

export const getJobs = <T>() => handleGetRequest<T>('Job/GetJobs')
export const getTicketMonitorTickets = <T>(data: any) => handleGetRequest<T>('TicketMonitor/GetTickets', data)
export const getTicketMonitorFilterOptions = <T>() => handleGetRequest<T>('TicketMonitor/GetFilterOptions')
export const getTicketMonitorRoundAudit = <T>(data: any) => handleGetRequest<T>('TicketMonitor/GetRoundAudit', data)

export const restartJob = <T>(data: { Name: string }) => handlePostRequest<T>('Job/RestartJob', data)

export const getGoogleRatingReport = (data: any) => {
  return axiosService.get('/csm/Report/GetGoogleRatingReport', data)
}

export const getGoogleRatingList = (data: any) => {
  return axiosService.get('/csm/Report/GetGoogleRatingList', data)
}

export const getSocialMediasByGroupId = (data: any) => {
  return axiosService.get('/csm/SocialMedia/GetSocialMediasByGroupId', data)
}

export const getTemplatesByGroupId = (data: any) => {
  return axiosService.get('/csm/Feedback/GetTemplatesByGroupId', data)
}

export const getFunctionalityTypes = (data: any) => {
  return axiosService.get('/csm/Entity/GetFunctionalityTypes', data)
}

export const getEntityTypes = <T>(data?: any) => handleGetRequest<T>('Entity/GetEntityTypes', data)

export const getEntityTypesGroupBySections = (data: any) => {
  return axiosService.get('/csm/Entity/GetEntityTypesGroupBySections', data)
}

export const getHubTypesGroupBySections = (data: any) => {
  return axiosService.get('/csm/QrCode/GetHubTypesGroupBySections', data)
}

export const getEntitiesByGroupId = <T>(data: any) => {
  return axiosService.get<T>('/csm/Entity/GetEntitiesByGroupId', data)
}

export const getPropertyDetail = <T>(data?: any) => handleGetRequest<T>('Entity/GetPropertyDetail', data)
// Remembers the building for the next session; the backend takes the user from
// the token and refuses a building they cannot already reach.
export const setDefaultBuilding = <T>(data: { BuildingId: string }) =>
  handlePostRequest<T>('Profile/SetDefaultBuilding', data)
// Nav-style competition: stamps Preference.NavStyle ('Legacy' | 'OneClick')
// on the caller's User record. Telemetry only — nothing reads it back.
export const setNavStyle = <T>(data: { NavStyle: string }) => handlePostRequest<T>('Profile/SetNavStyle', data)
// specs#3375: notification contact method (Text / Email / Both) for
// due-ticket alerts, surfaced from the PM Compliance dashboard's Alerts button.
// Building-scoped: governs every assignee of the building's PM tickets.
// Missing/null is treated as "Both" by the caller.
// gId is the account on screen (specs#3466): an Activate admin's session
// group is frozen at login and does not follow the header account switch, so
// without it these calls answered "Building not found in this account".
export const getDueTicketNotificationPreference = <T>(data: { bId: string; gId?: string }) =>
  handleGetRequest<T>('Entity/GetDueTicketNotificationPreference', data)
export const saveDueTicketNotificationPreference = <T>(data: {
  BuildingId: string
  ContactMethod: string
  GroupId?: string
}) => handlePostRequest<T>('Entity/SaveDueTicketNotificationPreference', data)
// Module Start Dates (specs#3498): per building, per module, the calendar day
// from which that module's data for the building exists anywhere. Anyone in the
// account may read them (dashboards grey their pre-start columns); only Activate
// admins may save (Entity.AcAdminMethods). Date is "YYYY-MM-DD" in the
// building's own timezone; the backend anchors it at local midnight.
export type ModuleStartDateKey = 'SR' | 'OD' | 'Inspections' | 'PM' | 'Engineering' | 'Cleaning' | 'Security'
export interface ModuleStartDateITF {
  Ms: number
  Date: string
  UpdatedBy: string | null
  UpdatedAt: number | null
}
export interface ModuleStartDatesITF {
  BuildingId: string
  Timezone: string
  Modules?: { Key: ModuleStartDateKey; Label: string }[]
  StartDates: Record<ModuleStartDateKey, ModuleStartDateITF | null>
}
export const getModuleStartDates = <T = ModuleStartDatesITF>(data: { bId: string; gId?: string }) =>
  handleGetRequest<T>('Entity/GetModuleStartDates', data)
export const saveModuleStartDate = <T = ModuleStartDatesITF>(data: {
  BuildingId: string
  GroupId?: string
  Key: ModuleStartDateKey
  Date: string | null
}) => handlePostRequest<T>('Entity/SaveModuleStartDate', data)
// Building Info -> Operations -> Room Reservation (specs#3442). Activate-internal
// only: the backend gates both on IsAcAdmin (Entity.AcAdminMethods), so a
// customer session gets a 403 whatever the UI does.
export const getRoomRequestSettings = <T>(data: { bId: string; gId?: string }) =>
  handleGetRequest<T>('Entity/GetRoomRequestSettings', data)
export const saveRoomRequestSettings = <T>(data: {
  BuildingId: string
  GroupId?: string
  RoomRequest: {
    Enabled: boolean
    ApproverEmails: string[]
    Buffer?: { Enabled: boolean; PreMinutes: number; PostMinutes: number }
    WorkOrders?: {
      Setup: { Enabled: boolean; Team?: string; AssigneeId?: string; AssigneeName?: string }
      Cleanup: { Enabled: boolean; Team?: string; AssigneeId?: string; AssigneeName?: string }
    }
  }
}) => handlePostRequest<T>('Entity/SaveRoomRequestSettings', data)

export const getGroupAllImages = (data: any) => {
  return axiosService.get('/csm/User/GetGroupAllImages', data)
}

export const getQrStylesImages = (data: any) => {
  return axiosService.get('/csm/QrCode/GetQrStylesImages', data)
}

export const globalAdminGenerateQr = (data: any) => {
  return axiosService.post('/csm/QrCode/GlobalAdminGenerateQr', data)
}

export const visitorLogin = <T>(data: any) => handlePostRequest<T>('User/VisitorLogin', data)
export const checkInUser = <T>(data?: { GroupId?: string }) => handlePostRequest<T>('User/CheckIn', data)

export const loginUser = <T>(data: any) => {
  return axiosService.post<T>('/csm/User/AdminLogin', data)
}

export const getGenericEntitiesForFeedback = <T>(data: any) => {
  return axiosService.get<T>('/csm/Entity/GetGenericEntitiesForFeedback', data)
}

export const getQrCode = <T>(data: any) => {
  return axiosService.get<T>('/csm/QrCode/Scan', data)
}

export const getTemplateById = <T>(data: any) => {
  return axiosService.get<T>('/csm/Feedback/GetTemplateById', data)
}

export const viewAnalytics = <T>(data: any) => {
  return axiosService.get<T>('/csm/Analytics/ViewAnalytics', data)
}

export const viewEntitiesAnalytics = <T>(data: any) => {
  return axiosService.get<T>('/csm/Analytics/ViewEntitiesAnalytics', data)
}

export const viewOverallEntityTypeAnalytics = <T>(data: any) => {
  return axiosService.get<T>('/csm/Analytics/ViewOverallEntityTypeAnalytics', data)
}

export const getCommentWordClouds = <T>(data: any) => {
  return axiosService.get<T>('/csm/Analytics/GetCommentWordClouds', data)
}

export const getEntityComments = <T>(data: any) => {
  return axiosService.get<T>('/csm/Analytics/GetEntityComments', data)
}

export const getTopicSummary = <T>(data: any) => {
  return axiosService.get<T>('/csm/Analytics/GetTopicSummary', data)
}

export const getGroups = <T>(data?: any) => {
  return axiosService.get<T>('/csm/Group/GetGroups', data)
}

export const uploadBase64 = (data: any) => {
  return axiosService.post('/csm/QrCode/UploadBase64', data)
}

export const imageUrlToBase64 = (data: any) => {
  return axiosService.get('/csm/QrCode/ImageUrlToBase64', data)
}

export const uploadQrBuilderFile = (data: any) => {
  return axiosService.post('/csm/Provision/UploadQrBuilderFile', data)
}

export const getCommunicationTemplatesByGroupId = (data: any) => {
  return axiosService.get('/csm/Communication/GetTemplatesByGroupId', data)
}

export const getPurchaseTemplatesByGroupId = (data: any) => {
  return axiosService.get('/csm/Purchase/GetTemplatesByGroupId', data)
}

export const getRentalTemplatesByGroupId = (data: any) => {
  return axiosService.get('/csm/Rental/GetTemplatesByGroupId', data)
}

export const getFieldMarketingTemplates = (data: any) => {
  return axiosService.get('/csm/FieldMarketing/GetTemplatesByGroupId', data)
}

export const getEventTemplates = (data: any) => {
  return axiosService.get('/csm/Event/GetTemplatesByGroupId', data)
}

export const getRenewalTemplates = (data: any) => {
  return axiosService.get('/csm/Renewal/GetTemplatesByGroupId', data)
}

export const getMaintenanceTemplates = (data: any) => {
  return axiosService.get('/csm/Maintenance/GetTemplatesByGroupId', data)
}

export const getMaintenanceRequests = (data: any) => {
  return axiosService.get('/csm/Maintenance/GetMaintenanceRequests', data)
}

export const getFrontDeskTemplates = (data: any) => {
  return axiosService.get('/csm/FrontDesk/GetFrontDeskTemplates', data)
}

export const getSupplierCheckinTemplates = (data: any) => {
  return axiosService.get('/csm/SupplierCheck/GetTemplatesByGroupId', data)
}

export const createProperty = (data: any) => {
  return axiosService.post('/csm/Entity/CreateProperty', data)
}

export const createAccount = (data: any) => {
  return axiosService.post('/csm/Group/CreateGroup', data)
}

export const getBuildingTypes = (data: any) => {
  return axiosService.get('/csm/Entity/GetBuildingTypes', data)
}

export const editProperty = (data: any) => {
  return axiosService.post('/csm/Entity/EditProperty', data)
}

export const editAccount = (data: any) => {
  return axiosService.post('/csm/Group/UpdateGroup', data)
}

export const generateQrStyle = (data: any) => {
  return axiosService.post('/csm/QrCode/GenerateQrStyle', data)
}

export const generateAndUploadQrStyle = (data: any) => {
  return axiosService.post('/csm/QrCode/GenerateAndUploadQrStyle', data)
}

export const GetRoles = (data: any) => {
  return axiosService.get('/csm/Member/GetRoles', data)
}

export const GetCities = (data: any) => {
  return axiosService.post('/csm/Geo/GetCities', data)
}

export const GetStates = (data: any) => {
  return axiosService.get('/csm/Geo/GetStates', data)
}

export const GetPropertyContacts = <T>(data: any) => {
  return axiosService.get<T>('/csm/Entity/GetPropertyContacts', data)
}

export const SaveEntity = <T>(data: any) => {
  return axiosService.post<T>('/csm/Entity/SaveEntity', data)
}

export const addPropertyContact = <T>(data: any) => {
  return axiosService.post<T>('/csm/UserAdmin/AddPropertyContact', data)
}
export const editPropertyContact = <T>(data: any) => {
  return axiosService.post<T>('/csm/UserAdmin/EditPropertyContact', data)
}
export const deletePropertyContact = (data: any) => {
  return axiosService.post('/csm/UserAdmin/DeletePropertyContact', data)
}
export const generatePropertyEntitiesAndQrs = (data: any) => {
  return axiosService.post('/csm/Entity/GeneratePropertyEntitiesAndQrs', data)
}

export const getEventRatingSummary = (data: any) => handleGetRequest<any>('Analytics/GetEventRatingSummary', data)
export const getEventComments = (data: any) => handleGetRequest('Analytics/GetEventComments', data)
export const getPageById = (data: any) => handleGetRequest('Page/GetPageById', data)
export const updatePageById = (data: any) => handlePostRequest('Page/UpdatePageById', data)
export const getEmergenciesByGroupId = (data: any) => handleGetRequest('Notice/GetEmergenciesByGroupId', data)
export const getVacanciesByGroupId = (data: any) => handleGetRequest('Vacancy/GetVacanciesByGroupId', data)
export const getDigitalQrHub = (data: any) => handleGetRequest('QrCode/GetDigitalQrHub', data)
export const saveHub = <T>(data: any) => handlePostRequest<T>('QrCode/SaveHub', data)
export const getQrHubsGroupBySections = (data: any) => handleGetRequest('QrCode/GetQrHubsGroupBySections', data)
export const getHubQrCodeInfo = (data: any) => handleGetRequest('QrCode/GetHubQrCodeInfo', data)
export const getTemplateDataById = (data: any) => handleGetRequest('QrCode/GetTemplateById', data)
export const getSimpleTemplatesByGroupId = (data: any) => handleGetRequest('Form/GetSimpleTemplatesByGroupId', data)
export const getMoveInTemplatesByGroupId = (data: any) => handleGetRequest('Move/GetMoveInTemplatesByGroupId', data)
export const getMoveOutTemplatesByGroupId = (data: any) => handleGetRequest('Move/GetMoveOutTemplatesByGroupId', data)
export const getCustomerSupportTemplatesByGroupId = (data: any) =>
  handleGetRequest('Support/GetTemplatesByGroupId', data)
export const getQrCodeDetail = (data: any) => handleGetRequest('QrCode/GetQrCodeDetail', data)
export const getQrCodeTypesGroupBySections = (data: any) =>
  handleGetRequest('QrCode/GetQrCodeTypesGroupBySections', data)
export const globalAdminUpdateQr = (data: any) => handlePostRequest('QrCode/GlobalAdminUpdateQr', data)
export const getFeedbackQrCodesEntityTypes = (data: any) =>
  handleGetRequest('QrCode/GetFeedbackQrCodesEntityTypes', data)
export const getFilterInfoByPropertyId = (data: any) => handleGetRequest('QrCode/GetFilterInfoByPropertyId', data)
export const getFunctionalityTypeDefaultTemplate = <T>(data: any) =>
  handleGetRequest<T>('QrCode/GetFunctionalityTypeDefaultTemplate', data)
export const getPropertyList = <T>(data: any) => handleGetRequest<T>('Entity/GetPropertyList', data)
export const getGroup = <T>(data: any) => handleGetRequest<T>('Group/GetGroup', data)
// Account Info -> Analytics. Activate-internal only — the backend gates both on
// IsAcAdmin (Group.AcAdminMethods), so a customer session gets a 403 whatever
// the UI does.
export const getAnalyticsSettings = <T>(data?: { gId?: string }) =>
  handleGetRequest<T>('Group/GetAnalyticsSettings', data)
export const saveAnalyticsSettings = <T>(data: {
  GroupId: string
  FeatureFlags?: { FeatureName: string; FeatureEnabled: boolean; Buildings?: string[] }[]
}) => handlePostRequest<T>('Group/UpdateAnalyticsSettings', data)
export const getQrTypesFlat = <T>(data?: any) => handleGetRequest<T>('QrCode/GetQrTypesFlat', data)
export const generatePrintKit = <T>(data?: any) => handlePostRequest<T>('QrCode/GeneratePrintKit', data)
export const getPrintKits = <T>(data?: any) => handleGetRequest<T>('QrCode/GetPrintKits', data)
export const downloadQrCodesZipFile = <T>(data?: any) => handlePostRequest<T>('QrCode/DownloadQrCodesZipFile', data)
export const archiveQrCodeByIds = <T>(data?: any) => handlePostRequest<T>('QrCode/ArchiveQrCodeByIds', data)
export const fireExtinguisherSaveTemplateFields = <T>(data?: any) =>
  handlePostRequest<T>('FireExtinguisher/SaveTemplateFields', data)
export const AEDSaveTemplateFields = <T>(data?: any) => handlePostRequest<T>('AED/SaveTemplateFields', data)
export const getQrHubIcons = <T>() => handleGetRequest<T>('QrCode/GetQrHubIcons', {})
export const getIcons = <T>(data?: any) => handleGetRequest<T>('UploadFile/GetIcons', data)
export const getNotifyRuleChoices = <T>(data?: any) => handleGetRequest<T>('QrCode/GetNotifyRuleChoices', data)
export const saveNotifyRule = <T>(data?: any) => handlePostRequest<T>('QrCode/SaveNotifyRule', data)
export const savePropertyNotifyRule = <T>(data?: any) => handlePostRequest<T>('QrCode/SavePropertyNotifyRule', data)
export const getNotifyRule = <T>(data?: any) => handleGetRequest<T>('QrCode/GetNotifyRule', data)
export const emailQrCode = <T>(data?: any) => handlePostRequest<T>('QrCode/EmailQrCode', data)
export const ensureQrSvg = <T>(data?: any) => handlePostRequest<T>('QrCode/EnsureQrSvg', data)
export const getBathroomServiceReport = <T>(data?: any) =>
  handleGetRequest<T>('Analytics/GetBathroomServiceReport', data)
export const getBathroomServiceTopics = <T>(data?: any) =>
  handleGetRequest<T>('Analytics/GetBathroomServiceTopics', data)
export const getBathroomServiceWordClouds = <T>(data?: any) =>
  handleGetRequest<T>('Analytics/GetBathroomServiceWordClouds', data)
export const getBathroomServiceComments = <T>(data?: any) =>
  handleGetRequest<T>('Analytics/GetBathroomServiceComments', data)
export const getPrintTemplates = <T>(data?: any) => handleGetRequest<T>('UploadFile/GetPrintTemplates', data)
export const deletePropertyContacts = <T>(data?: any) => handlePostRequest<T>('UserAdmin/DeletePropertyContacts', data)
export const getRounds = <T>(data?: any) => handleGetRequest<T>('Round/GetRounds', data)
export const saveRound = <T>(data?: any) => handlePostRequest<T>('Round/SaveRound', data)
export const getRoundById = <T>(data?: any) => handleGetRequest<T>('Round/GetRoundById', data)
export const archiveRounds = <T>(data?: any) => handlePostRequest<T>('Round/ArchiveRounds', data)
export const getRoundTypes = <T>(data?: any) => handleGetRequest<T>('Round/GetRoundTypes', data)
export const qrCodePair = <T>(data?: any) => handlePostRequest<T>('QrCode/Pair', data)
export const getPair = <T>(data?: any) => handleGetRequest<T>('QrCode/GetPair', data)
export const getReportDateRangeOptions = <T>(data?: any) => handleGetRequest<T>('Round/GetReportDateRangeOptions', data)
export const getDayRangeOptions = <T>(data?: any) => handleGetRequest<T>('Round/GetDayRangeOptions', data)
export const getComplianceOverview = <T>(data?: any) => handleGetRequest<T>('Round/GetComplianceOverview', data)
export const getRoundInspectionSummaries = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetRoundInspectionSummaries', data)
export const getRoundExceptionReport = <T>(data?: any) => handleGetRequest<T>('Round/GetExceptionReport', data)
export const deleteSubscription = <T>(data?: any) => handlePostRequest<T>('Subscription/Archive', data)
export const createSubscription = <T>(data?: any) => handlePostRequest<T>('Subscription/Create', data)
export const updateSubscription = <T>(data?: any) => handlePostRequest<T>('Subscription/Update', data)
export const getQuestionOnBuilding = <T>(data?: any) => handleGetRequest<T>('Round/GetQuestionOnBuilding', data)
export const saveAEDQuestion = <T>(data?: any) => handlePostRequest<T>('Round/SaveTemplateInfoOnBuilding', data)
export const uploadRoundTemplateFile = <T>(data?: any) =>
  handlePostRequest<T>('Provision/UploadRoundTemplateFile', data)
export const resolveRoundTemplateFile = <T>(data?: any) =>
  handlePostRequest<T>('Provision/ResolveRoundTemplateFile', data)
export const uploadFileBase64 = <T>(data?: any) => handlePostRequest<T>('Provision/UploadFileBase64', data)
export const uploadEquipmentFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadEquipmentFile', data)
export const getEquipmentFilterInfo = <T>(data?: any) => handleGetRequest<T>('Equipment/GetEquipmentFilterInfo', data)
export const getMaintenanceWordClouds = <T>(data?: any) => handleGetRequest<T>('Maintenance/GetDetailWordClouds', data)
export const getSentimentsByRequestType = <T>(data?: any) =>
  handleGetRequest<T>('Maintenance/GetSentimentsByRequestType', data)
export const getMaintenanceFeedbacks = <T>(data?: any) =>
  handleGetRequest<T>('Maintenance/GetMaintenanceFeedbacks', data)
export const getFeedbackAvgScore = <T>(data?: any) => handleGetRequest<T>('Maintenance/GetFeedbackAvgScore', data)
export const getTenantHealthDashboard = <T>(data?: any) =>
  handleGetRequest<T>('Maintenance/GetTenantHealthDashboard', data)
export const getMaintenanceSummary = <T>(data?: any) => handleGetRequest<T>('Maintenance/GetFeedbackSummary', data)
export const getTenantsRequestSummaryList = <T>(data?: any) =>
  handleGetRequest<T>('Maintenance/GetTenantsRequestSummaryList', data)
export const getMaintenanceSummaries = <T>(data?: any) =>
  handleGetRequest<T>('Maintenance/GetMaintenanceFeedbackSummaries', data)
export const getMaintenanceAnalysisSummaryForGroup = <T>(data?: any) =>
  handleGetRequest<T>('Maintenance/GetMaintenanceAnalysisSummaryForGroup', data)
export const getMaintenanceAnalysisForGroup = <T>(data?: any) =>
  handleGetRequest<T>('Maintenance/GetMaintenanceAnalysisForGroup', data)
export const getFeedbackWordClouds = <T>(data?: any) => handleGetRequest<T>('Maintenance/GetFeedbackWordClouds', data)
export const getDefaultQuestions = <T>(data?: any) => handleGetRequest<T>('Equipment/GetDefaultQuestions', data)
export const saveDefaultQuestions = <T>(data?: any) => handlePostRequest<T>('Equipment/SaveDefaultQuestions', data)
export const getCustomerFeedbackSummary = <T>(data?: any) =>
  handleGetRequest<T>('Feedback/GetCustomerFeedbackSummary', data)
export const getCustomerFeedbackSummariesList = <T>(data?: any) =>
  handleGetRequest<T>('Feedback/GetCustomerFeedbackSummariesList', data)
export const getCustomerFeedbacks = <T>(data?: any) => handleGetRequest<T>('Feedback/GetCustomerFeedbacks', data)
// specs#3439: cross-customer Customer Feedback dashboard (all rows for the window; the page filters).
export const getCustomerFeedbackDashboard = <T>(data: { startDate?: number; endDate?: number }) =>
  handleGetRequest<T>('Feedback/GetCustomerFeedbackDashboard', data)
export const uploadContactFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadContactFile', data)
export const uploadVendorFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadVendorFile', data)
export const uploadPropertyFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadPropertyFile', data)
export const getPropertyVendors = <T>(data?: any) => handleGetRequest<T>('Vendor/GetPropertyVendors', data)
export const archiveVendors = <T>(data?: any) => handlePostRequest<T>('Vendor/ArchiveVendors', data)
export const createVendor = <T>(data?: any) => handlePostRequest<T>('Vendor/CreateVendor', data)
export const editVendor = <T>(data?: any) => handlePostRequest<T>('Vendor/EditVendor', data)
export const getCheckOverview = <T>(data?: any) => handleGetRequest<T>('Vendor/GetCheckOverview', data)
export const getCheckSummary = <T>(data?: any) => handleGetRequest<T>('Vendor/GetCheckSummary', data)
export const forgetPassword = <T>(data?: any) => handlePostRequest<T>('User/ForgetPassword', data)
// linkToken binds the call to a specific reset link (multiple reset emails/tabs
// may be open; the shared storage token must not decide which link is consumed).
export const resetPassword = <T>(data?: any, linkToken?: string) =>
  axiosService.post<T>('/csm/User/ResetPassword', data, linkToken ? { headers: { UserToken: linkToken } } : undefined)
export const savePins = <T>(data?: any) => handlePostRequest<T>('Pin/SavePins', data)
export const getAngusAreas = <T>(data?: any) => handleGetRequest<T>('Maintenance/GetAngusAreas', data)
export const uploadInspectionFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadInspectionFile', data)
export const getInspectionReportForGroup = <T>(data?: any) =>
  handleGetRequest<T>('Inspection/GetInspectionReportForGroup', data)
export const getInspectionAggregate = <T>(data?: any) => handleGetRequest<T>('Inspection/GetInspectionAggregate', data)
export const getQrTypeNotifyRules = <T>(data?: any) => handleGetRequest<T>('QrCode/GetQrTypeNotifyRules', data)
export const getNotifyRuleChoicesByType = <T>(data?: any) =>
  handleGetRequest<T>('QrCode/GetNotifyRuleChoicesByType', data)
export const submitReportRequest = <T>(data?: any) => handlePostRequest<T>('Report/SubmitReportRequest', data)
export const getGroupSurveyCompletion = <T>(data?: any) => handleGetRequest<T>('Survey/GetGroupSurveyCompletion', data)
export const getCustomerSurvey = <T>(data?: any) => handleGetRequest<T>('Survey/GetCustomerSurvey', data)
export const getSurveyBuilder = <T>(data?: any) => handleGetRequest<T>('Survey/GetSurveyBuilder', data)
export const saveSurveyBuilder = <T>(data?: any) => handlePostRequest<T>('Survey/SaveSurveyBuilder', data)
export const getCustomerSurveyVerify = <T>(data?: any) => handleGetRequest<T>('Survey/GetCustomerSurveyVerify', data)
export const saveCustomerSurveyVerify = <T>(data?: any) => handlePostRequest<T>('Survey/SaveCustomerSurveyVerify', data)
export const saveCustomerSurvey = <T>(data?: any) => handlePostRequest<T>('Survey/SaveCustomerSurvey', data)
export const getSurveyConnectList = <T>(data?: any) => handleGetRequest<T>('Survey/GetSurveyConnectList', data)
export const updateConnectHubItem = <T>(data?: any) => handlePostRequest<T>('Survey/UpdateConnectHubItem', data)
export const saveConnectContent = <T>(data?: any) => handlePostRequest<T>('Survey/SaveConnectContent', data)
export const generateConnectQr = <T>(data?: any) => handlePostRequest<T>('Survey/GenerateConnectQr', data)
export const downloadEquipmentsZipFile = <T>(data?: any) =>
  handlePostRequest<T>('Equipment/DownloadEquipmentsZipFile', data)
export const submitDashboardReportRequest = <T>(data?: any) =>
  handlePostRequest<T>('Report/SubmitDashboardReportRequest', data)
export const submitEmailReportRequest = <T>(data?: any) => handlePostRequest<T>('Report/SubmitEmailReportRequest', data)
export const getCheckRecordOverview = <T>(data?: any) =>
  handleGetRequest<T>('SupplierCheck/GetCheckRecordOverview', data)
export const getCheckRecordSummary = <T>(data?: any) => handleGetRequest<T>('SupplierCheck/GetCheckRecordSummary', data)
export const downloadCheckRecordSummary = <T>(data?: any) =>
  handleGetRequest<T>('SupplierCheck/DownloadCheckRecordSummary', data)
export const saveTenant = <T>(data?: any) => handlePostRequest<T>('Tenant/SaveTenant', data)
export const submitLeaseAbstraction = <T>(data?: any) => handlePostRequest<T>('LeaseAbstraction/Submit', data)
export const getLeaseAbstractionByTenant = <T>(data?: any) => handleGetRequest<T>('LeaseAbstraction/GetByTenant', data)
export const removeLeaseDocument = <T>(data?: any) => handlePostRequest<T>('LeaseAbstraction/RemoveDocument', data)
export const getLeaseAbstractionBuildingReport = <T>(data?: any) =>
  handleGetRequest<T>('LeaseAbstraction/GetBuildingReport', data)
export const getLeaseAbstractionGroupReport = <T>(data?: any) =>
  handleGetRequest<T>('LeaseAbstraction/GetGroupReport', data)
export const regenerateLeaseAbstractions = <T>(data?: any) =>
  handlePostRequest<T>('LeaseAbstraction/RegenerateBuilding', data)
export const getSubscriptionTypes = <T>(data?: any) => handlePostRequest<T>('Subscription/GetSubscriptionTypes', data)
export const getCurrentCarouselHub = <T>(data?: any) => handleGetRequest<T>('Survey/GetCurrentCarouselHub', data)
export const saveCarousel = <T>(data?: any) => handlePostRequest<T>('Survey/SaveCarousel', data)
export const getInstallationRoundDetail = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetInstallationRoundDetail', data)
export const downloadInstallationDetail = <T>(data?: any) =>
  handleGetRequest<T>('Round/DownloadInstallationDetail', data)
export const downloadGlobalPrintSetting = <T>(data?: any) =>
  handleGetRequest<T>('PrintManagement/DownloadGlobalPrintSetting', data)
export const downloadQrCodesPrintOrders = <T>(data?: any) =>
  handlePostRequest<T>('PrintManagement/DownloadQrCodesPrintOrders', data)
export const uploadTenantFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadTenantFile', data)
export const uploadResidentFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadResidentFile', data)
export const uploadTenantContactFile = <T>(data?: any) =>
  handlePostRequest<T>('Provision/UploadTenantContactFile', data)
export const getTenantList = <T>(data?: any) => handleGetRequest<T>('Tenant/GetTenantList', data)
export const getTenantCompanies = <T>(data?: any) => handleGetRequest<T>('Tenant/GetTenantCompanies', data)
export const searchTenantCompanies = <T>(data?: any) => handleGetRequest<T>('Tenant/SearchTenantCompanies', data)
export const assignTenantCompany = <T>(data?: any) => handlePostRequest<T>('Tenant/AssignTenantCompany', data)
export const getTenantPinCode = <T>(data?: any) => handleGetRequest<T>('Tenant/GetPinCode', data)
export const uploadGlobalPrintSettingFile = <T>(data?: any) =>
  handlePostRequest<T>('Provision/UploadGlobalPrintSettingFile', data)
export const archiveGlobalPrintSetting = <T>(data?: any) =>
  handlePostRequest<T>('PrintManagement/ArchiveGlobalPrintSetting', data)
export const savePrintProofs = <T>(data?: any) => handlePostRequest<T>('PrintManagement/SavePrintProofs', data)
export const getWoTasksByWoId = <T>(data?: any) => handleGetRequest<T>('WorkOrder/GetWoTasksByWoId', data)
export const createActivateOndemandWorkOrder = <T>(data?: any) =>
  handlePostRequest<T>('WorkOrder/CreateActivateOndemandWorkOrder', data)
export const updateTasklines = <T>(data?: any) => handlePostRequest<T>('WorkOrder/UpdateTasklines', data)
export const getPMWorkOrdersExcel = <T>(data?: any) => handleGetRequest<T>('WorkOrder/GetPMWorkOrdersExcel', data)
export const getHtmlTemplates = <T>(data?: any) => handleGetRequest<T>('Page/GetHtmlTemplates', data)
export const saveHtmlTemplate = <T>(data?: any) => handlePostRequest<T>('Page/SaveHtmlTemplate', data)
export const getGroupCOIReport = <T>(data?: any) => handleGetRequest<T>('COI/GetGroupCOIReport', data)
export const getBuildingCOIReport = <T>(data?: any) => handleGetRequest<T>('COI/GetBuildingCOIReport', data)
export const requestCOIByEmail = <T>(data?: any) => handlePostRequest<T>('Vendor/RequestCOIByEmail', data)
export const getVendorCOIByContact = <T>(data?: any) => handleGetRequest<T>('COI/GetVendorCOIByContact', data)
export const getCOIByContact = <T>(data?: any) => handleGetRequest<T>('COI/GetCOIByContact', data)
// Supplier-facing COI upload page (updateCOI). Uses a visitor token, so unlike
// getCOIByContact it is not gated on the caller's building access.
export const getCOIForSupplierUpload = <T>(data?: any) => handleGetRequest<T>('COI/GetCOIForSupplierUpload', data)
export const submitCOI = <T>(data?: any) => handlePostRequest<T>('COI/Submit', data)
// Saves a tenant's COI on its own, without running the tenant form's validation.
export const saveTenantCOI = <T>(data?: any) => handlePostRequest<T>('COI/SaveTenantCOI', data)
export const reviewCOI = <T>(data?: any) => handlePostRequest<T>('COI/Review', data)
export const getCoiDetail = <T>(data?: any) => handleGetRequest<T>('COI/GetCoiDetail', data)
// Insurance dashboard modal: the certificate with its read and comparison (specs#3471),
// scoped on the server to the caller's group and buildings.
export const getCoiReview = <T>(data: { cId: string; gId?: string }) => handleGetRequest<T>('COI/GetCoiReview', data)
export const reparseCOI = <T>(data: { CoiId: string; GroupId?: string }) => handlePostRequest<T>('COI/ReparseCOI', data)
export const getTwoQrLocations = <T>(data?: any) => handleGetRequest<T>('QrCode/GetTwoQrLocations', data)
export const getTwoQRTypesByLocation = <T>(data?: any) => handleGetRequest<T>('QrCode/GetTwoQRTypesByLocation', data)
export const archiveQrPair = <T>(data?: any) => handlePostRequest<T>('QrCode/ArchiveQrPair', data)
export const getQrCodesByGroupId = <T>(data?: any) => handleGetRequest<T>('QrCode/GetQrCodesByGroupId', data)
export const getLocationScanStats = <T>(data?: any) => handleGetRequest<T>('QrCode/GetLocationScanStats', data)
export const getMaintenances = <T>(data?: any) => handleGetRequest<T>('Maintenance/GetMaintenances', data)
export const getEquipmentsByMSpaceId = <T>(data?: any) => handleGetRequest<T>('Equipment/GetEquipmentsByMSpaceId', data)
export const getRoundsByMSpaceId = <T>(data?: any) => handleGetRequest<T>('Round/GetRoundsByMSpaceId', data)
export const getConversationSentiments = <T>(data?: any) =>
  handleGetRequest<T>('Maintenance/GetConversationSentiments', data)
export const getBathroomComplianceOverview = <T>(data?: any) =>
  handleGetRequest<T>('Bathroom/GetComplianceOverview', data)
export const getServiceFeedbacks = <T>(data?: any) => handleGetRequest<T>('Bathroom/GetServiceFeedbacks', data)
export const getBathroomActions = <T>(data?: any) => handleGetRequest<T>('Bathroom/GetBathroomActions', data)
export const getCleaningIQ = <T>(data?: any) => handleGetRequest<T>('CleaningIQ/GetCleaningIQ', data)
// Split from the stats call: this is the only one that can reach OpenAI, so the page
// renders its numbers first and lets the narrative arrive after.
export const getCleaningIQSummary = <T>(data?: any) => handleGetRequest<T>('CleaningIQ/GetCleaningIQSummary', data)
// Engineering IQ (specs#3482): same split as Cleaning IQ, stats then narrative.
export const getEngineeringIQ = <T>(data?: any) => handleGetRequest<T>('EngineeringIQ/GetEngineeringIQ', data)
export const getEngineeringIQSummary = <T>(data?: any) =>
  handleGetRequest<T>('EngineeringIQ/GetEngineeringIQSummary', data)
// Security IQ (specs#3482): same split, stats then narrative.
export const getSecurityIQ = <T>(data?: any) => handleGetRequest<T>('SecurityIQ/GetSecurityIQ', data)
export const getSecurityIQSummary = <T>(data?: any) => handleGetRequest<T>('SecurityIQ/GetSecurityIQSummary', data)
export const getRoundInspectionsAndEquipments = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetRoundInspectionsAndEquipments', data)
export const saveRoundAssigner = <T>(data?: any) => handlePostRequest<T>('Round/SaveRoundAssigner', data)
export const saveStopInstructionNotes = <T>(data?: any) => handlePostRequest<T>('Round/SaveStopInstructionNotes', data)
export const updateEquipmentInfo = <T>(data?: any) => handlePostRequest<T>('Equipment/UpdateEquipmentInfo', data)
export const getMoveOuts = <T>(data?: any) => handleGetRequest<T>('Move/GetMoveOuts', data)
export const getMoveOutSummary = <T>(data?: any) => handleGetRequest<T>('Move/GetMoveOutSummary', data)
export const getEngineerRoundInspectionSummaries = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetEngineerRoundInspectionSummaries', data)
export const getWorkOrderComplianceOverview = <T>(data?: any) =>
  handleGetRequest<T>('WorkOrder/GetComplianceOverview', data)
export const getScheduledWorkOrders = <T>(data?: any) => handleGetRequest<T>('WorkOrder/GetScheduledWorkOrders', data)
export const getInspectionRoundReport = <T>(data?: any) => handleGetRequest<T>('Round/GetInspectionRoundReport', data)
export const getInspectionSnapshotYearRange = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetInspectionSnapshotYearRange', data)
export const getRoundSummariesList = <T>(data?: any) => handleGetRequest<T>('Round/GetRoundSummariesList', data)
export const getMemberRoles = <T>(data?: any) => handleGetRequest<T>('Member/GetMemberRoles', data)
export const getWorkOrderEquipmentTypes = <T>(data?: any) =>
  handleGetRequest<T>('WorkOrder/GetWorkOrderEquipmentTypes', data)
export const getFilterInfo = <T>(data?: any) => handleGetRequest<T>('PrintManagement/GetFilterInfo', data)
export const addPropertyTag = <T>(data?: any) => handlePostRequest<T>('Entity/AddPropertyTag', data)
export const getPropertyTags = <T>(data?: any) => handleGetRequest<T>('Entity/GetPropertyTags', data)
export const archivePropertyTag = <T>(data?: any) => handlePostRequest<T>('Entity/ArchivePropertyTag', data)
export const getGlobalFilterInfo = <T>(data?: any) => handleGetRequest<T>('QrCode/GetGlobalFilterInfo', data)
export const checkQrSignId = <T>(data?: any) => handleGetRequest<T>('QrCode/CheckQrSignId', data)
export const getPinActivities = <T>(data?: any) => handleGetRequest<T>('Pin/GetPinActivities', data)
export const getIncidentReports = <T>(data?: any) => handleGetRequest<T>('Incident/GetIncidentReports', data)
export const getIncidentTemplate = <T>(data?: any) => handleGetRequest<T>('Incident/GetIncidentTemplate', data)
export const submitIncidentReport = <T>(data?: any) => handlePostRequest<T>('Incident/SubmitIncidentReport', data)
// Polish button (3537): {Text} in, {Text, Polished} out; Text is unchanged when Polished is false.
export const polishIncidentText = <T>(data?: any) => handlePostRequest<T>('Incident/PolishText', data)
export const getGroupRoundSummaries = <T>(data?: any) => handleGetRequest<T>('Round/GetGroupRoundSummaries', data)
export const getGroupIncidentReports = <T>(data?: any) => handleGetRequest<T>('Incident/GetGroupIncidentReports', data)
export const getEquipmentList = <T>(data?: any) => handleGetRequest<T>('Equipment/GetEquipmentList', data)
export const getEquipments = <T>(data?: any) => handleGetRequest<T>('Equipment/GetEquipments', data)
export const getEquipmentIQ = <T>(data?: any) => handleGetRequest<T>('EquipmentIQ/GetEquipmentIQ', data)
export const generateBuildingEquipmentIQ = <T>(data?: any) => handlePostRequest<T>('EquipmentIQ/GenerateBuilding', data)
export const getBuildingEquipmentIQStatus = <T>(data?: any) =>
  handleGetRequest<T>('EquipmentIQ/GetBuildingStatus', data)
// DEV-ONLY (3191 IQ cache viewer)
export const listEquipmentIQCache = <T>(data?: any) => handleGetRequest<T>('EquipmentIQ/ListCache', data)
// DEV-ONLY (3191 IQ cache viewer)
export const getEquipmentIQCacheEntry = <T>(data?: any) => handleGetRequest<T>('EquipmentIQ/GetCacheEntry', data)
// DEV-ONLY (3191 IQ cache viewer)
export const deleteEquipmentIQCacheEntry = <T>(data?: any) => handlePostRequest<T>('EquipmentIQ/DeleteCacheEntry', data)
export const getEquipmentTrends = <T>(data?: any) => handleGetRequest<T>('Equipment/GetEquipmentTrends', data)
export const getPMsByEquipmentId = <T>(data?: any) => handleGetRequest<T>('Round/GetPMsByEquipmentId', data)
export const getPMEquipmentCounts = <T>(data?: any) => handleGetRequest<T>('Round/GetPMEquipmentCounts', data)
export const getInspectionHistory = <T>(data?: any) => handleGetRequest<T>('Equipment/GetInspectionHistory', data)

// ---- Capital Planning (reads the Equipment Library; never writes it) ----
// Enrichment trigger — async worker job. Whole building by default (Equipment Library), or only
// equipmentIds (dashboard Enrich modal); reopen = re-enrich already-resolved models in the selection.
export const enrichEquipmentDocuments = <T>(data: {
  groupId: string
  buildingId: string
  equipmentIds?: string[]
  reopen?: boolean
}) =>
  handlePostRequest<T>('CapitalPlanning/EnrichEquipmentDocuments', {
    GroupId: data.groupId,
    BuildingId: data.buildingId,
    EquipmentIds: data.equipmentIds,
    Reopen: data.reopen,
  })
export const getEquipmentDocEnrichStatus = <T>(data: { groupId?: string; buildingId: string }) =>
  handleGetRequest<T>('CapitalPlanning/GetEquipmentDocEnrichStatus', { gId: data.groupId, bId: data.buildingId })
// Per-make/model enrichment state for a building — drives the dashboard Enrich modal + polling.
export const getCapitalEnrichModelStatuses = <T>(data: { groupId: string; buildingId: string }) =>
  handleGetRequest<T>('CapitalPlanning/GetEnrichModelStatuses', { gId: data.groupId, bId: data.buildingId })
// Dashboard reads — live-computed on the backend per request (the "Snapshot" names are legacy).
export const getBuildingPlanSnapshot = <T>(data: { groupId: string; buildingId: string }) =>
  handleGetRequest<T>('CapitalPlanning/GetBuildingPlanSnapshot', { gId: data.groupId, bId: data.buildingId })
export const getPortfolioPlanSnapshot = <T>(data: { groupId: string }) =>
  handleGetRequest<T>('CapitalPlanning/GetPortfolioPlanSnapshot', { gId: data.groupId })
export const getCapitalPlanningTelemetry = <T>(data?: {
  gId?: string
  bId?: string
  epId?: string
  step?: string
  since?: number
  limit?: number
}) => handleGetRequest<T>('CapitalPlanning/GetTelemetry', data)
// PM Compliance dashboards (portfolio + building) — single year-scoped read.
export const getPMComplianceData = <T>(data: { gId: string; bId?: string; year?: number }) =>
  handleGetRequest<T>('PMCompliance/GetComplianceData', data)
// Inspection Compliance (portfolio Inspection Overview) — single year-scoped read.
export const getInspectionComplianceData = <T>(data: { gId: string; bId?: string; year?: number }) =>
  handleGetRequest<T>('InspectionCompliance/GetComplianceData', data)
// PM Task Details + Edit PM Steps (specs#3541). Both return the task detail shape
// (pmCompliance/pmcShared PMTaskDetail); the save returns the refreshed detail.
export const getPMTaskDetail = <T>(data: { gId?: string; rId: string }) =>
  handleGetRequest<T>('PMCompliance/GetPMTaskDetail', data)
export const savePMTaskSteps = <T>(data: {
  gId?: string
  RoundId: string
  // The edited checklist group's anchor stop (PMTaskStepGroup.StopId); omitted = first group.
  StopId?: string
  // Write the list to every equipment stop on the round, not only the edited group.
  ApplyToAll?: boolean
  ContentVersion: number
  Steps: { Source: number | null; Question: string; QuestionType?: string; InheritFrom?: number | null }[]
}) => handlePostRequest<T>('PMCompliance/SavePMTaskSteps', data)
// Cure Photos for the Inspection Overview table's current page (#3501). ids is the
// comma-joined RoundInspection ids of the walks on screen; returns { [id]: urls[] }.
export const getInspectionWalkPhotos = <T>(data: { gId: string; bId?: string; ids: string }) =>
  handleGetRequest<T>('InspectionCompliance/GetWalkPhotos', data)
export const getScanTrendData = <T>(data?: any) => handleGetRequest<T>('QrCode/GetScanTrendData', data)
export const getInspectionWithStops = <T>(data?: any) => handleGetRequest<T>('Round/GetInspectionWithStops', data)
export const getRoundInspectionsInfo = <T>(data?: any) => handleGetRequest<T>('Round/GetRoundInspectionsInfo', data)
export const sendPortalAccess = <T>(data?: any) => handlePostRequest<T>('UserAdmin/SendPortalAccess', data)
export const getCurrentPrintSettings = <T>(data?: any) =>
  handleGetRequest<T>('PrintManagement/GetCurrentPrintSettings', data)
export const getPrintSettingFilter = <T>(data?: any) =>
  handleGetRequest<T>('PrintManagement/GetPrintSettingFilter', data)
export const getBuildingInfo = <T>(data?: any) => handleGetRequest<T>('Entity/GetBuildingInfo', data)
export const getGoogleRatingOverReview = <T>(data?: any) =>
  handleGetRequest<T>('Report/GetGoogleRatingOverReview', data)
export const getPrescriptiveData = <T>(data?: any) => handleGetRequest<T>('Report/GetPrescriptiveData', data)
export const getInspectionWithStopForms = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetInspectionWithStopForms', data)
export const equipmentSubmit = <T>(data?: any) => handlePostRequest<T>('Equipment/Submit', data)
export const createDeficiency = <T>(data?: any) => handlePostRequest<T>('Round/CreateDeficiency', data)
export const updateDeficiency = <T>(data?: any) => handlePostRequest<T>('Round/UpdateDeficiency', data)
export const generateInspectionPdfRequest = <T>(data?: any) =>
  handlePostRequest<T>('Round/GenerateInspectionPdfRequest', data)
export const emailInspectionPdf = <T>(data?: any) => handlePostRequest<T>('Round/EmailInspectionPdf', data)
export const saveProductQuestions = <T>(data?: any) => handlePostRequest<T>('Survey/SaveProductQuestions', data)
export const submitAdditionalAns = <T>(data?: any) => handlePostRequest<T>('Survey/SubmitAdditionalAns', data)
export const downloadSurveyFieldFiles = <T>(data?: any) => handlePostRequest<T>('Survey/DownloadSurveyFieldFiles', data)
export const getIncidentInfo = <T>(data?: any) => handleGetRequest<T>('Incident/GetIncidentInfo', data)
export const submitReportBuilderRequest = <T>(data?: any) =>
  handlePostRequest<T>('Report/SubmitReportBuilderRequest', data)
export const getReportBuilder = <T>(data?: any) => handleGetRequest<T>('Report/GetReportBuilder', data)
export const emailReportBuilderPdf = <T>(data?: any) => handlePostRequest<T>('Report/EmailReportBuilderPdf', data)
export const copyRound = <T>(data?: any) => handlePostRequest<T>('Round/CopyRound', data)
export const copyQrCodes = <T>(data?: any) => handlePostRequest<T>('QrCode/CopyQrCodes', data)
export const getCheckRecordSummariesFilter = <T>(data?: any) =>
  handleGetRequest<T>('SupplierCheck/GetCheckRecordSummariesFilter', data)
export const getUpsellProducts = <T>(data?: any) => handleGetRequest<T>('Entity/GetUpsellProducts', data)
export const getPropertyCharacteristics = <T>(data?: any) =>
  handleGetRequest<T>('Entity/GetPropertyCharacteristics', data)
export const saveProductDetail = <T>(data?: any) => handlePostRequest<T>('Entity/SaveProductDetail', data)
export const getUpsellMatrix = <T>(data?: any) => handleGetRequest<T>('Analytics/GetUpsellMatrix', data)
export const createSolutionBuilder = <T>(data?: any) => handlePostRequest<T>('Solution/CreateSolutionBuilder', data)
export const getBuildingSolutionBuilder = <T>(data?: any) =>
  handleGetRequest<T>('Solution/GetBuildingSolutionBuilder', data)
export const getSolutionBuilderById = <T>(data?: any) => handleGetRequest<T>('Solution/GetSolutionBuilderById', data)
export const subscribeReportBuilder = <T>(data?: any) => handlePostRequest<T>('Report/SubscribeReportBuilder', data)
export const updatePrintProofNote = <T>(data?: any) =>
  handlePostRequest<T>('PrintManagement/UpdatePrintProofNote', data)
export const updateCarouselNote = <T>(data?: any) => handlePostRequest<T>('Survey/UpdateCarouselNote', data)
export const getQrHubsByGroupId = <T>(data?: any) => handleGetRequest<T>('QrCode/GetQrHubsByGroupId', data)
export const getWorkOrderTemplates = <T>(data?: any) => handleGetRequest<T>('WorkOrder/GetWorkOrderTemplates', data)
export const getExceptionReports = <T>(data?: any) => handleGetRequest<T>('Report/GetExceptionReports', data)
export const addPortfolioContact = <T>(data?: any) => handlePostRequest<T>('UserAdmin/AddPortfolioContact', data)
export const editPortfolioContact = <T>(data?: any) => handlePostRequest<T>('UserAdmin/EditPortfolioContact', data)
export const getContactDetail = <T>(data?: any) => handleGetRequest<T>('UserAdmin/GetContactDetail', data)
export const deletePortfolioContacts = <T>(data?: any) =>
  handlePostRequest<T>('Portfolio/DeletePortfolioContacts', data)
export const getVendorCOI = <T>(data?: any) => handleGetRequest<T>('COI/GetVendorCOI', data)
export const deleteBuilderSubscription = <T>(data?: any) =>
  handlePostRequest<T>('Report/DeleteBuilderSubscription', data)
export const updateBuilderSubscription = <T>(data?: any) =>
  handlePostRequest<T>('Report/UpdateBuilderSubscription', data)
export const getSurveyList = <T>(data?: any) => handleGetRequest<T>('Survey/GetSurveyList', data)
export const getBuildingActiveQrHubs = <T>(data?: any) => handleGetRequest<T>('QrCode/GetBuildingActiveQrHubs', data)
export const sendPortalSurveyAccess = <T>(data?: any) => handlePostRequest<T>('UserAdmin/SendPortalSurveyAccess', data)
export const getLegalInfo = <T>(data?: any) => handleGetRequest<T>('LeadCapture/GetLegalInfo', data)
export const saveCarouselNote = <T>(data?: any) => handlePostRequest<T>('Survey/SaveCarouselNote', data)
export const savePrintProofNote = <T>(data?: any) => handlePostRequest<T>('PrintManagement/SavePrintProofNote', data)
export const getResidentCheckOverview = <T>(data?: any) =>
  handleGetRequest<T>('LeadCapture/GetResidentCheckOverview', data)
export const getResidentCheckList = <T>(data?: any) => handleGetRequest<T>('LeadCapture/GetResidentCheckList', data)
export const getTenantUnits = <T>(data?: any) => handleGetRequest<T>('Tenant/GetTenantUnits', data)
export const getBuildingsContacts = <T>(data?: any) => handleGetRequest<T>('Entity/GetBuildingsContacts', data)
export const getDateArchiveFilterInfo = <T>(data?: any) =>
  handleGetRequest<T>('DataArchive/GetDateArchiveFilterInfo', data)
export const getRequestById = <T>(data?: any) => handleGetRequest<T>('OpenAI/GetRequestById', data)
export const getCheckSummaryGroupByBuilding = <T>(data?: any) =>
  handleGetRequest<T>('MobileEmployee/GetCheckSummaryGroupByBuilding', data)
export const getCheckSummaryGroupList = <T>(data?: any) =>
  handleGetRequest<T>('MobileEmployee/GetCheckSummaryGroupList', data)
export const getChecksList = <T>(data?: any) => handleGetRequest<T>('SupplierCheck/GetChecksList', data)
export const getGuestCheckOverview = <T>(data?: any) => handleGetRequest<T>('SupplierCheck/GetCheckOverview', data)
export const getInspectionForWorkLetter = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetInspectionForWorkLetter', data)
export const saveWorkLetter = <T>(data?: any) => handlePostRequest<T>('Round/SaveWorkLetter', data)
export const getTenantLetterDefault = <T>(data?: any) => handleGetRequest<T>('Round/GetTenantLetterDefault', data)
export const saveQrCustomerTemplate = <T>(data?: any) => handlePostRequest<T>('QrCode/SaveQrCustomerTemplate', data)
export const getWorkLetterDetail = <T>(data?: any) => handleGetRequest<T>('Round/GetWorkLetterDetail', data)
export const getSolutionBuilderQrs = <T>(data?: any) => handleGetRequest<T>('Solution/GetSolutionBuilderQrs', data)
export const getPropertyCHARFilter = <T>(data?: any) => handleGetRequest<T>('Entity/GetPropertyCHARFilter', data)
export const getPropertyCharSectionFilter = <T>(data?: any) =>
  handleGetRequest<T>('Entity/GetPropertyCharSectionFilter', data)
export const getPropertyCHARList = <T>(data?: any) => handleGetRequest<T>('Entity/GetPropertyCHARList', data)
export const savePropertyCharacteristics = <T>(data?: any) =>
  handlePostRequest<T>('Entity/SavePropertyCharacteristics', data)
export const getFeedbackRules = <T>(data?: any) => handleGetRequest<T>('Feedback/GetFeedbackRules', data)
export const getProductNames = <T>(data?: any) => handleGetRequest<T>('Feedback/GetProductNames', data)
export const saveFeedbackRule = <T>(data?: any) => handlePostRequest<T>('Feedback/SaveFeedbackRule', data)
export const archiveFeedbackRule = <T>(data?: any) => handlePostRequest<T>('Feedback/ArchiveFeedbackRule', data)
// specs#3439: ad-hoc survey formats (Feedback Engine rules with Trigger Adhoc) and
// the emailed "rate Activate" invite sent from Contacts -> Property Team. Recipients
// go up as UserIds only; the backend re-resolves addresses itself.
export const getAdhocFeedbackRules = <T>() => handleGetRequest<T>('Feedback/GetFeedbackRules', { trigger: 'Adhoc' })
export const sendPropertyTeamFeedbackInvites = <T>(data: {
  GroupId: string
  BuildingId: string
  FeedbackRuleId: string
  UserIds: string[]
  Subject: string
  Body: string
}) => handlePostRequest<T>('Feedback/SendPropertyTeamFeedbackInvites', data)
export const copySurveyBuilder = <T>(data?: any) => handlePostRequest<T>('Survey/CopySurveyBuilder', data)
export const getEquipmentTypes = <T>(data?: any) => handleGetRequest<T>('Equipment/GetEquipmentTypes', data)
export const getCarouselHubInfo = <T>(data?: any) => handleGetRequest<T>('Survey/GetCarouselHubInfo', data)
export const getQrCarouselHub = <T>(data?: any) => handleGetRequest<T>('Survey/GetQrCarouselHub', data)
export const getActivityFilter = <T>(data?: any) => handleGetRequest<T>('MobileEmployee/GetActivityFilter', data)
export const getActivityList = <T>(data?: any) => handleGetRequest<T>('MobileEmployee/GetActivityList', data)
export const downloadHtmlPdf = <T>(data?: any) => handlePostRequest<T>('UploadFile/DownloadHtmlPdf', data)
export const getBuildingResidentFeedbackSummary = <T>(data?: any) =>
  handleGetRequest<T>('Feedback/GetBuildingResidentFeedbackSummary', data)
export const getResidentFeedbackSummary = <T>(data?: any) =>
  handleGetRequest<T>('Feedback/GetResidentFeedbackSummary', data)
export const saveGlobalPrintSetting = <T>(data?: any) =>
  handlePostRequest<T>('PrintManagement/SaveGlobalPrintSetting', data)
export const getProducts = <T>(data?: any) => handleGetRequest<T>('Program/GetProducts', data)
export const saveProductCheck = <T>(data?: any) => handlePostRequest<T>('Program/SaveProductCheck', data)
export const saveProductNotes = <T>(data?: any) => handlePostRequest<T>('Program/SaveProductNotes', data)
export const saveCategory = <T>(data?: any) => handlePostRequest<T>('Program/SaveCategory', data)
export const updateCategory = <T>(data?: any) => handlePostRequest<T>('Program/UpdateCategory', data)
export const getHubItemLogData = <T>(data?: any) => handleGetRequest<T>('QrCode/GetHubItemLogData', data)
export const getFilterInfoBathroom = <T>(data?: any) => handleGetRequest<T>('Bathroom/GetFilterInfo', data)
export const getResidentIndex = <T>(data?: any) => handleGetRequest<T>('Entity/GetResidentIndex', data)
export const getMaterialCostAndGLCodes = <T>(data?: any) =>
  handleGetRequest<T>('Entity/GetMaterialCostAndGLCodes', data)
export const saveResidentIndex = <T>(data?: any) => handlePostRequest<T>('Entity/SaveResidentIndex', data)
export const saveCollectContent = <T>(data?: any) => handlePostRequest<T>('Survey/SaveCollectContent', data)
export const getInstallationGuide = <T>(data?: any) => handleGetRequest<T>('Entity/GetInstallationGuide', data)
export const saveInstallationGuide = <T>(data?: any) => handlePostRequest<T>('Entity/SaveInstallationGuide', data)
export const uploadMasterMigrationFile = <T>(data?: any) =>
  handlePostRequest<T>('Provision/UploadMasterMigrationFile', data)
export const getAllPromptList = <T>(data?: any) => handleGetRequest<T>('OpenAI/GetAllPromptList', data)
export const savePrompt = <T>(data?: any) => handlePostRequest<T>('OpenAI/SavePrompt', data)
export const getPromptType = <T>(data?: any) => handleGetRequest<T>('OpenAI/GetPromptType', data)
export const getLookbooks = <T>(data?: any) => handleGetRequest<T>('Lookbook/GetLookbooks', data)
export const getLookbookFilterInfo = <T>(data?: any) => handleGetRequest<T>('Lookbook/GetFilterInfo', data)
export const logout = <T>(data?: any) => handlePostRequest<T>('User/Logout', data)
export const getFeedbackActions = <T>(data?: any) => handleGetRequest<T>('Feedback/GetFeedbackActions', data)
export const getCustomFeedbackSummary = <T>(data?: any) =>
  handleGetRequest<T>('Feedback/GetCustomFeedbackSummary', data)
export const getCustomWordClouds = <T>(data?: any) => handleGetRequest<T>('Feedback/GetCustomWordClouds', data)
export const getCustomTopicSummary = <T>(data?: any) => handleGetRequest<T>('Feedback/GetCustomTopicSummary', data)
export const getCustomFeedbackComments = <T>(data?: any) =>
  handleGetRequest<T>('Feedback/GetCustomFeedbackComments', data)
export const getAggregateCarouselNotes = <T>(data?: any) =>
  handleGetRequest<T>('Survey/GetAggregateCarouselNotes', data)
export const getAggregatePrintProofNotes = <T>(data?: any) =>
  handleGetRequest<T>('PrintManagement/GetAggregatePrintProofNotes', data)
export const getPortfolioContacts = <T>(data?: any) => handleGetRequest<T>('Portfolio/GetPortfolioContacts', data)
export const sendMultiBuilders = <T>(data?: any) => handlePostRequest<T>('Survey/SendMultiBuilders', data)
export const getMultiBuilder = <T>(data?: any) => handleGetRequest<T>('Survey/GetMultiBuilder', data)
export const getSurveyBuilderStatus = <T>(data?: any) => handleGetRequest<T>('Survey/GetSurveyBuilderStatus', data)
export const getRoundsWorkFlowFilter = <T>(data?: any) => handleGetRequest<T>('Round/GetRoundsWorkFlowFilter', data)
export const getRoundsWorkFlowList = <T>(data?: any) => handleGetRequest<T>('Round/GetRoundsWorkFlowList', data)
export const getInspectionTypes = <T>(data?: any) => handleGetRequest<T>('Round/GetInspectionTypes', data)
export const getInspectionTypesByBuilding = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetInspectionTypesByBuilding', data)
export const saveInspectionTypeConfig = <T>(data?: any) => handlePostRequest<T>('Round/SaveInspectionTypeConfig', data)
export const deleteInspectionTypeConfig = <T>(data?: any) =>
  handlePostRequest<T>('Round/DeleteInspectionTypeConfig', data)
export const getTeamPerformanceOverView = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetTeamPerformanceOverView', data)
export const getTeamPerformanceInspectionSummaries = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetTeamPerformanceInspectionSummaries', data)
export const getTeamPerformanceExceptions = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetTeamPerformanceExceptions', data)
export const getTeamPerformanceSummaries = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetTeamPerformanceSummaries', data)
export const getActivityReport = <T>(data?: any) => handleGetRequest<T>('Round/GetActivityReport', data)
export const getBuildingCOIList = <T>(data?: any) => handleGetRequest<T>('COI/GetBuildingCOIList', data)
export const sendUpdateEmail = <T>(data?: any) => handlePostRequest<T>('COI/SendUpdateEmail', data)
export const getIncidentType = <T>(data?: any) => handleGetRequest<T>('Incident/GetIncidentType', data)
export const getTrades = <T>(data?: any) => handleGetRequest<T>('Vendor/GetTrades', data)
export const saveMaterialCostAndGLCodes = <T>(data?: any) =>
  handlePostRequest<T>('Entity/SaveMaterialCostAndGLCodes', data)
export const listMaterialCostFields = <T>(data?: any) => handleGetRequest<T>('Entity/ListMaterialCostFields', data)
export const saveMaterialCostField = <T>(data?: any) => handlePostRequest<T>('Entity/SaveMaterialCostField', data)
export const deleteMaterialCostField = <T>(data?: any) => handlePostRequest<T>('Entity/DeleteMaterialCostField', data)
export const listGLCodeFields = <T>(data?: any) => handleGetRequest<T>('Entity/ListGLCodeFields', data)
export const saveGLCodeField = <T>(data?: any) => handlePostRequest<T>('Entity/SaveGLCodeField', data)
export const deleteGLCodeField = <T>(data?: any) => handlePostRequest<T>('Entity/DeleteGLCodeField', data)
export const getTicketFieldAggregate = <T>(data?: any) => handleGetRequest<T>('Ticket/GetTicketFieldAggregate', data)
export const getDashboardTicketList = <T>(data?: any) => handleGetRequest<T>('Ticket/GetDashboardTicketList', data)
export const getTicketOverview = <T>(data?: any) => handleGetRequest<T>('Ticket/GetTicketOverview', data)
// Work Order Intelligence (3310). The report always spans every channel, so these take no
// view/type param — the dashboard's active view scopes the table, never the report.
export const getWorkOrderReportPreview = <T>(data?: any) =>
  handleGetRequest<T>('WorkOrderIntelligence/GetReportPreview', data)
export const getWorkOrderReportData = <T>(data?: any) =>
  handleGetRequest<T>('WorkOrderIntelligence/GetReportData', data)
export const generateWorkOrderReportWorkbook = <T>(data?: any) =>
  handleGetRequest<T>('WorkOrderIntelligence/GenerateWorkbook', data)
export const generateWorkOrderReportPdf = <T>(data?: any) =>
  handleGetRequest<T>('WorkOrderIntelligence/GeneratePdf', data)
export const emailWorkOrderReport = <T>(data?: any) => handlePostRequest<T>('WorkOrderIntelligence/EmailReport', data)
// Tenant Feedback Intelligence (specs#3488). Input-free beyond the dashboard's identity:
// gId, an optional bIds, and the group's display name for the subtitle.
export const getTenantFeedbackReportData = <T>(data?: any) =>
  handleGetRequest<T>('TenantFeedbackIntelligence/GetReportData', data)
export const generateTenantFeedbackReportPdf = <T>(data?: any) =>
  handleGetRequest<T>('TenantFeedbackIntelligence/GeneratePdf', data)
export const emailTenantFeedbackReport = <T>(data?: any) =>
  handlePostRequest<T>('TenantFeedbackIntelligence/EmailReport', data)
// PM Intelligence (specs#3509): the report the PM Compliance dashboard's Intelligence
// button opens in a modal. gId, an optional bId, and the group's display name.
export const getPMIntelligenceReportData = <T>(data?: any) => handleGetRequest<T>('PMIntelligence/GetReportData', data)
export const uploadBEPropertyTeamFile = <T>(data?: any) =>
  handlePostRequest<T>('Provision/UploadBEPropertyTeamFile', data)
export const uploadBETenantUserFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadBETenantUserFile', data)
export const uploadBEVendorUserFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadBEVendorUserFile', data)
export const uploadBETenantFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadBETenantFile', data)
export const uploadBEVendorCompanyFile = <T>(data?: any) =>
  handlePostRequest<T>('Provision/UploadBEVendorCompanyFile', data)
export const uploadBECOIFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadBECOIFile', data)
export const uploadBEEquipmentFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadBEEquipmentFile', data)
export const uploadBEInspectionFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadBEInspectionFile', data)
export const uploadBERateFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadBERateFile', data)
export const uploadBEMaterialFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadBEMaterialFile', data)
export const uploadBEPMProcedureFile = <T>(data?: any) =>
  handlePostRequest<T>('Provision/UploadBEPMProcedureFile', data)
export const uploadBEPMScheduleFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadBEPMScheduleFile', data)
export const uploadBEPMTaskFile = <T>(data?: any) => handlePostRequest<T>('Provision/UploadBEPMTaskFile', data)
export const startBeMigration = (data: StartBeMigrationRequest) =>
  handlePostRequest<StartBeMigrationResponse>('Provision/StartBeMigration', data)
export const getBeMigrationStatus = (data: GetBeMigrationStatusRequest) =>
  handleGetRequest<RunStatus>('Provision/GetBeMigrationStatus', data)
export const listBeMigrationRuns = (data: ListBeMigrationRunsRequest) =>
  handleGetRequest<ListBeMigrationRunsResponse>('Provision/ListBeMigrationRuns', data)
export const resumeBeStep = (data: ResumeBeStepRequest) =>
  handlePostRequest<ResumeBeStepResponse>('Provision/ResumeBeStep', data)
export const getImportUploadDependencies = (data?: GetImportUploadDependenciesRequest) =>
  handleGetRequest<GetImportUploadDependenciesResponse>('Provision/GetImportUploadDependencies', data)
export const uploadImpakMasterFile = (data: UploadImpakMasterFileRequest) =>
  handlePostRequest<UploadImpakMasterFileResponse>('Provision/UploadImpakMasterFile', data)
export const listImpakMasterFiles = (data: ListImpakMasterFilesRequest) =>
  handleGetRequest<ListImpakMasterFilesResponse>('Provision/ListImpakMasterFiles', data)
export const deleteImpakMasterFile = (data: DeleteImpakMasterFileRequest) =>
  handlePostRequest<DeleteImpakMasterFileResponse>('Provision/DeleteImpakMasterFile', data)
export const startImpakMigration = (data: StartImpakMigrationRequest) =>
  handlePostRequest<StartImpakMigrationResponse>('Provision/StartImpakMigration', data)
export const getImpakMigrationStatus = (data: GetBeMigrationStatusRequest) =>
  handleGetRequest<RunStatus>('Provision/GetImpakMigrationStatus', data)
export const listImpakMigrationRuns = (data: ListBeMigrationRunsRequest) =>
  handleGetRequest<ListBeMigrationRunsResponse>('Provision/ListImpakMigrationRuns', data)
export const resumeImpakStep = (data: ResumeBeStepRequest) =>
  handlePostRequest<ResumeBeStepResponse>('Provision/ResumeImpakStep', data)
export const uploadAngusMasterFile = (data: UploadAngusMasterFileRequest) =>
  handlePostRequest<UploadAngusMasterFileResponse>('Provision/UploadAngusMasterFile', data)
export const listAngusMasterFiles = (data: ListAngusMasterFilesRequest) =>
  handleGetRequest<ListAngusMasterFilesResponse>('Provision/ListAngusMasterFiles', data)
export const deleteAngusMasterFile = (data: DeleteAngusMasterFileRequest) =>
  handlePostRequest<DeleteAngusMasterFileResponse>('Provision/DeleteAngusMasterFile', data)
export const startAngusMigration = (data: StartAngusMigrationRequest) =>
  handlePostRequest<StartAngusMigrationResponse>('Provision/StartAngusMigration', data)
export const getAngusMigrationStatus = (data: GetBeMigrationStatusRequest) =>
  handleGetRequest<RunStatus>('Provision/GetAngusMigrationStatus', data)
export const listAngusMigrationRuns = (data: ListBeMigrationRunsRequest) =>
  handleGetRequest<ListBeMigrationRunsResponse>('Provision/ListAngusMigrationRuns', data)
export const resumeAngusStep = (data: ResumeBeStepRequest) =>
  handlePostRequest<ResumeBeStepResponse>('Provision/ResumeAngusStep', data)
export const saveCOI = <T>(data?: any) => handlePostRequest<T>('COI/SaveCOI', data)
export const setIntegrityBaseline = <T>(data?: any) => handlePostRequest<T>('Equipment/SetIntegrityBaseline', data)
export const getIntegrityPreview = <T>(data?: any) => handleGetRequest<T>('Equipment/GetIntegrityPreview', data)
export const getPortfolioIntegrity = <T>(data?: any) => handleGetRequest<T>('Equipment/GetPortfolioIntegrity', data)
export const sendSurveyEmail = <T>(data?: any) => handlePostRequest<T>('UserAdmin/SendSurveyEmail', data)
export const loginByPinCode = <T>(data?: any) => handlePostRequest<T>('User/LoginByPinCode', data)
export const archivedSurveyBuilderById = <T>(data?: any) =>
  handlePostRequest<T>('Survey/ArchivedSurveyBuilderById', data)
export const notifyOnboard = <T>(data?: any) => handlePostRequest<T>('UserAdmin/NotifyOnboard', data)
export const getCarouselNotes = <T>(data?: any) => handleGetRequest<T>('Survey/GetCarouselNotes', data)
export const getCarouselNotesFilter = <T>(data?: any) => handleGetRequest<T>('Survey/GetCarouselNotesFilter', data)
export const maintenanceSubmit = <T>(data?: any) => handlePostRequest<T>('Maintenance/StartAndSubmit', data)
export const getBillableTicketList = <T>(data?: any) => handleGetRequest<T>('Ticket/GetBillableTicketList', data)
export const getInvoiceList = <T>(data?: any) => handleGetRequest<T>('Ticket/GetInvoiceList', data)
export const generateBillableInvoiceReport = <T>(data?: any) =>
  handlePostRequest<T>('Ticket/GenerateBillableInvoiceReport', data)
// QR Engineering tickets (specs#3398)
export const getQrTickets = <T>(data?: any) => handleGetRequest<T>('Survey/GetQrTickets', data)
export const getQrTicketOverview = <T>(data?: any) => handleGetRequest<T>('Survey/GetQrTicketOverview', data)
export const getQrTicketReport = <T>(data?: any) => handleGetRequest<T>('Survey/GetQrTicketReport', data)
export const saveQrTicket = <T>(data?: any) => handlePostRequest<T>('Survey/SaveQrTicket', data)
export const updateQrTicket = <T>(data?: any) => handlePostRequest<T>('Survey/UpdateQrTicket', data)
export const archiveQrTicket = <T>(data?: any) => handlePostRequest<T>('Survey/ArchiveQrTicket', data)
export const saveQrTickets = <T>(data?: any) => handlePostRequest<T>('Survey/SaveQrTickets', data)
export const getAdminContacts = <T>(data?: any) => handleGetRequest<T>('UserAdmin/GetAdminContacts', data)

// ── User Offboarding (specs#3312) ────────────────────────────────────────────
export interface OffboardInfoITF {
  OffboardDate?: number
  OffboardedDate?: number
  OffboardedBy?: string
}
export interface OffboardUserResponseITF {
  AlreadyOffboarded?: boolean
  TokensRevoked?: number
  MembersArchived?: number
  ActionTokensExpired?: number
}
export const getOffboardInfo = <T = OffboardInfoITF>(data: { ContactUserId: string }) =>
  handleGetRequest<T>('UserAdmin/GetOffboardInfo', data)
export const offboardUser = <T = OffboardUserResponseITF>(data: { ContactUserId: string }) =>
  handlePostRequest<T>('UserAdmin/OffboardUser', data)
export const setOffboardDate = <T = { OffboardDate: number }>(data: { ContactUserId: string; OffboardDate: number }) =>
  handlePostRequest<T>('UserAdmin/SetOffboardDate', data)
export const cancelOffboard = <T = void>(data: { ContactUserId: string }) =>
  handlePostRequest<T>('UserAdmin/CancelOffboard', data)
export const saveTenantContact = <T>(data?: any) => handlePostRequest<T>('Tenant/SaveTenantContact', data)
// specs#3432: per-group picklists behind the tenant contact Role and Employee Tag
// combo boxes. Every call answers with the full { Roles, Tags } lists.
export const getTenantContactPicklists = <T = TenantContactPicklistsITF>(data: { gId: string }) =>
  handleGetRequest<T>('Tenant/GetTenantContactPicklists', data)
// Without PreviousValue it adds Value; with it, it renames PreviousValue to
// Value and the backend moves every contact that carried the old value.
export const saveTenantContactPicklistOption = <T = TenantContactPicklistsITF>(data: {
  GroupId: string
  Type: TenantContactPicklistType
  Value: string
  PreviousValue?: string
}) => handlePostRequest<T>('Tenant/SaveTenantContactPicklistOption', data)
// Drops the option from the list; contacts keep their stored value.
export const deleteTenantContactPicklistOption = <T = TenantContactPicklistsITF>(data: {
  GroupId: string
  Type: TenantContactPicklistType
  Value: string
}) => handlePostRequest<T>('Tenant/DeleteTenantContactPicklistOption', data)
export const getPMLibraryList = <T>(data?: any) => handleGetRequest<T>('Round/GetPMLibraryList', data)
export const getPMListFilter = <T>(data?: any) => handleGetRequest<T>('Round/GetPMListFilter', data)
export const getEquipmentManufactures = <T>(data?: any) =>
  handleGetRequest<T>('Equipment/GetEquipmentManufactures', data)
export const getBuildingInvoiceReportData = <T>(data?: any) =>
  handleGetRequest<T>('Ticket/GetBuildingInvoiceReportData', data)
export const getGroupInvoiceReportData = <T>(data?: any) =>
  handleGetRequest<T>('Ticket/GetGroupInvoiceReportData', data)
export const getGroupInvoiceLineItemData = <T>(data?: any) =>
  handleGetRequest<T>('Ticket/GetGroupInvoiceLineItemData', data)
export const getTenantInvoiceReportData = <T>(data?: any) =>
  handleGetRequest<T>('Ticket/GetTenantInvoiceReportData', data)
export const getBillableFieldAggregate = <T>(data?: any) =>
  handleGetRequest<T>('Ticket/GetBillableFieldAggregate', data)
export const getTicketsFilter = <T>(data?: any) => handleGetRequest<T>('Ticket/GetTicketsFilter', data)
// Every pill option for the Work Order dashboard in one read (specs#3497): keyed by
// view, then by the Ticket field each pill filters on. Takes only gId and the route
// building, so it is fetched once per page load, never per filter or page change.
export const getDashboardFilterOptions = <T>(data?: any) =>
  handleGetRequest<T>('Ticket/GetDashboardFilterOptions', data)
export const saveBuildingOption = <T>(data?: any) => handlePostRequest<T>('Entity/SaveBuildingOption', data)
export const getBuildingCoiInfo = <T>(data?: any) => handleGetRequest<T>('Entity/GetBuildingCoiInfo', data)
export const getGroupCoiInfo = <T>(data?: any) => handleGetRequest<T>('Group/GetGroupCoiInfo', data)
export const loadBrand = <T>(data?: any) => handleGetRequest<T>('User/LoadBrand', data)
export const getCalendarItems = <T>(data?: any) => handleGetRequest<T>('Calendar/GetCalendarItems', data)
export const updateBillableReportData = <T>(data?: any) => handlePostRequest<T>('Ticket/UpdateBillableReportData', data)
export const updateTicket = <T>(data: any) => handlePostRequest<T>('Ticket/UpdateTicket', data)
export const deleteTicket = <T>(data: any) => handlePostRequest<T>('Ticket/DeleteTicket', data)
export const saveBillable = <T>(data?: any) => handlePostRequest<T>('Ticket/SaveBillable', data)
export const getMaterialCost = <T>(data?: any) => handleGetRequest<T>('Entity/GetMaterialCost', data)
export const getRoundNames = <T>(data?: any) => handleGetRequest<T>('Round/GetRoundNames', data)
export const saveVisitorAppointment = <T>(data?: any) =>
  handlePostRequest<T>('SupplierCheck/SaveVisitorAppointment', data)
export const saveVisitorAppointments = <T>(data?: any) =>
  handlePostRequest<T>('SupplierCheck/SaveVisitorAppointments', data)
export const getVisitorChecks = <T>(data?: any) => handleGetRequest<T>('SupplierCheck/GetVisitorChecks', data)
export const getVisitorsSummary = <T>(data?: any) => handleGetRequest<T>('SupplierCheck/GetVisitorsSummary', data)
export const deleteVisitorAppointment = <T>(data?: any) =>
  handlePostRequest<T>('SupplierCheck/DeleteVisitAppointment', data)
export const updateVisitorAppointment = <T>(data?: any) =>
  handlePostRequest<T>('SupplierCheck/UpdateVisitAppointment', data)
export const getVisitAppointmentDetail = <T>(data?: any) =>
  handleGetRequest<T>('SupplierCheck/GetVisitAppointmentDetail', data)
export const searchVisitors = <T>(data?: any) => handleGetRequest<T>('SupplierCheck/SearchVisitors', data)
export const downloadVisitorChecks = <T>(data?: any) => handleGetRequest<T>('SupplierCheck/DownloadVisitorChecks', data)
export const getBadgePrintContext = <T>(data?: any) => handleGetRequest<T>('SupplierCheck/GetBadgePrintContext', data)
export const saveWatchlistEntry = <T>(data?: any) => handlePostRequest<T>('SupplierCheck/SaveWatchlistEntry', data)
export const getWatchlistEntries = <T>(data?: any) => handleGetRequest<T>('SupplierCheck/GetWatchlistEntries', data)
export const deleteWatchlistEntry = <T>(data?: any) => handlePostRequest<T>('SupplierCheck/DeleteWatchlistEntry', data)
export const getPendingCheckInApprovals = <T>(data?: any) =>
  handleGetRequest<T>('SupplierCheck/GetPendingCheckInApprovals', data)
export const resolveCheckInApproval = <T>(data?: any) =>
  handlePostRequest<T>('SupplierCheck/ResolveCheckInApproval', data)
// spec 3322: manual "Desk Check-In" for walk-ups from the Visitor Log row action
export const deskCheckIn = <T>(data?: any) => handlePostRequest<T>('SupplierCheck/DeskCheckIn', data)
// spec 3322: manual "Desk Check-Out" when a guest leaves early
export const deskCheckOut = <T>(data?: any) => handlePostRequest<T>('SupplierCheck/DeskCheckOut', data)
// specs 3314: registration-time review (non-blocking; deny also denies arrival)
export const reviewVisitAppointment = <T>(data?: any) =>
  handlePostRequest<T>('SupplierCheck/ReviewVisitAppointment', data)
// 865 S Figueroa: per-building visitor settings (QR self-registration toggle)
export const getVisitorSettings = <T>(data?: any) => handleGetRequest<T>('SupplierCheck/GetVisitorSettings', data)
export const saveVisitorSettings = <T>(data?: any) => handlePostRequest<T>('SupplierCheck/SaveVisitorSettings', data)
// spec 3322: host picker on the PendingApprovals security dashboard. Reuses the
// react-admin-style Tenant/GetTenantContacts endpoint (JSON-encoded query params).
export const searchHostContacts = <T>(data: { gId: string; bId?: string; q: string }) => {
  const query = [
    `pagination=${encodeURIComponent(JSON.stringify({ page: 0, perPage: 10 }))}`,
    `sort=${encodeURIComponent(JSON.stringify([]))}`,
    `filter=${encodeURIComponent(JSON.stringify({ gId: data.gId, bId: data.bId, st: data.q }))}`,
  ].join('&')
  return handleGetRequest<T>(`Tenant/GetTenantContacts?${query}`)
}
// specs#3399: every tenant contact for a building, for the onboarding send tree.
// Same react-admin-style endpoint as searchHostContacts, unpaginated.
export const getTenantContactsForBuilding = <T>(data: { gId: string; bId?: string }) => {
  const query = [
    `pagination=${encodeURIComponent(JSON.stringify({ page: 0, perPage: 10000 }))}`,
    `sort=${encodeURIComponent(JSON.stringify([]))}`,
    `filter=${encodeURIComponent(JSON.stringify({ gId: data.gId, bId: data.bId }))}`,
  ].join('&')
  return handleGetRequest<T>(`Tenant/GetTenantContacts?${query}`)
}
export const getServiceRequestType = <T>(data?: any) => handleGetRequest<T>('Maintenance/GetServiceRequestType', data)
export const getCharacterTemplate = <T>(data?: any) => handleGetRequest<T>('Entity/GetCharacterTemplate', data)
export const saveServiceRequestType = <T>(data?: any) =>
  handlePostRequest<T>('Maintenance/SaveServiceRequestType', data)
export const renameServiceRequestType = <T>(data?: any) =>
  handlePostRequest<T>('Maintenance/RenameServiceRequestType', data)
export const updateServiceRequestCategory = <T>(data?: any) =>
  handlePostRequest<T>('Maintenance/UpdateServiceRequestCategory', data)
export const createRelatedRoundStops = <T>(data?: any) =>
  handlePostRequest<T>('WorkOrder/CreateRelatedRoundStops', data)
export const getDeficiencyPortfolioList = <T>(data?: any) =>
  handleGetRequest<T>('Round/GetDeficiencyPortfolioList', data)
export const getDeficiencySummary = <T>(data?: any) => handleGetRequest<T>('Round/GetDeficiencySummary', data)
export const getBuildingDeficiencyList = <T>(data?: any) => handleGetRequest<T>('Round/GetBuildingDeficiencyList', data)
export const getLandlordDeficiencyList = <T>(data?: any) => handleGetRequest<T>('Round/GetLandlordDeficiencyList', data)
export const getTenantDeficiencyList = <T>(data?: any) => handleGetRequest<T>('Round/GetTenantDeficiencyList', data)
// Insurance Audit / Insurance Claim views of the Deficiency Tracker (specs#3477)
export const startInsuranceUpload = <T>(data?: any) => handlePostRequest<T>('InsuranceTracker/StartUpload', data)
export const getInsuranceUpload = <T>(data?: any) => handleGetRequest<T>('InsuranceTracker/GetUpload', data)
export const saveInsuranceAuditItems = <T>(data?: any) => handlePostRequest<T>('InsuranceTracker/SaveAuditItems', data)
export const getInsuranceAuditItems = <T>(data?: any) => handleGetRequest<T>('InsuranceTracker/GetAuditItems', data)
export const updateInsuranceAuditItem = <T>(data?: any) => handlePostRequest<T>('InsuranceTracker/UpdateAuditItem', data)
export const archiveInsuranceAuditItem = <T>(data?: any) => handlePostRequest<T>('InsuranceTracker/ArchiveAuditItem', data)
export const exportInsuranceAuditResponses = <T>(data?: any) =>
  handlePostRequest<T>('InsuranceTracker/ExportAuditResponses', data)
export const saveInsuranceClaim = <T>(data?: any) => handlePostRequest<T>('InsuranceTracker/SaveClaim', data)
export const getInsuranceClaims = <T>(data?: any) => handleGetRequest<T>('InsuranceTracker/GetClaims', data)
export const updateInsuranceClaim = <T>(data?: any) => handlePostRequest<T>('InsuranceTracker/UpdateClaim', data)
export const archiveInsuranceClaim = <T>(data?: any) => handlePostRequest<T>('InsuranceTracker/ArchiveClaim', data)
export const saveCharacterTemplate = <T>(data?: any) => handlePostRequest<T>('Entity/SaveCharacterTemplate', data)
export const uploadPropertyCharacterFile = <T>(data?: any) =>
  handlePostRequest<T>('Provision/UploadPropertyCharacterFile', data)
export const getResponsibilities = <T>(data?: any) => handleGetRequest<T>('Inspection/GetResponsibilities', data)
export const saveResponsibility = <T>(data?: any) => handlePostRequest<T>('Inspection/SaveResponsibility', data)
export const getCollectionItems = <T>(data?: any) => handlePostRequest<T>('Inspection/GetCollectionItems', data)
export const deleteResponsibility = <T>(data?: any) => handlePostRequest<T>('Inspection/DeleteResponsibility', data)
export const getTenantExecutiveSummary = <T>(data?: any) =>
  handleGetRequest<T>('Inspection/GetTenantExecutiveSummary', data)
export const getGroupTenantSummary = <T>(data?: any) =>
  handleGetRequest<T>('Inspection/GetGroupTenantExecutiveSummary', data)
export const deleteTenant = <T>(data?: any) => handlePostRequest<T>('Tenant/DeleteTenant', data)
export const archiveMember = <T>(data?: any) => handlePostRequest<T>('Member/ArchiveMember', data)
export const uploadPropertyTenantFile = <T>(data?: any) =>
  handlePostRequest<T>('Provision/UploadPropertyTenantFile', data)
export const getTicketDetail = <T>(data?: any) => handleGetRequest<T>('Ticket/GetTicketDetail', data)
export const getTicketInspectionMedia = <T>(data?: any) => handleGetRequest<T>('Ticket/GetTicketInspectionMedia', data)
export const getTicketHistory = <T>(data?: any) => handleGetRequest<T>('Ticket/GetTicketHistory', data)
// specs#3400: the BCC list last used for this building's onboarding sends.
// Stored on the building and rewritten by each send — there is no settings UI.
export const getOnboardingBcc = <T>(data: { gId?: string; bId?: string }) =>
  handleGetRequest<T>('Tenant/GetOnboardingBcc', data)
export const sendTenantOnboardingEmails = <T>(data?: any) =>
  handlePostRequest<T>('Tenant/SendTenantOnboardingEmails', data)
// Sent-email log. Group scope comes from the session on the backend, never from
// the query string — bId only narrows within the caller's own group.
export const getEmailLog = <T>(data: GetEmailLogRequest) => handleGetRequest<T>('Notification/GetEmailLog', data)
export const saveRentOption = <T>(data?: any) => handlePostRequest<T>('Rental/SaveRentOption', data)
export const getQrRentOption = <T>(data?: any) => handleGetRequest<T>('Rental/GetQrRentOption', data)
export const getBuildingReservations = <T>(data?: any) => handleGetRequest<T>('Rental/GetBuildingReservations', data)
export const getBuildingRentableLocations = <T>(data?: any) =>
  handleGetRequest<T>('Rental/GetBuildingRentableLocations', data)
export const cancelBooking = <T>(data?: any) => handlePostRequest<T>('Rental/CancelBooking', data)
// Room Request approval (specs#3318), staff-only on the backend.
export const approveBooking = <T>(data?: any) => handlePostRequest<T>('Rental/ApproveBooking', data)
export const rejectBooking = <T>(data?: any) => handlePostRequest<T>('Rental/RejectBooking', data)
export const saveRentBooking = <T>(data?: any) => handlePostRequest<T>('Rental/SaveRentBooking', data)
export const confirmBooking = <T>(data?: any) => handlePostRequest<T>('Rental/ConfirmBooking', data)
export const getBookingAlertRules = <T>(data?: any) => handleGetRequest<T>('Rental/GetBookingAlertRules', data)
export const saveBookingAlertRules = <T>(data?: any) => handlePostRequest<T>('Rental/SaveBookingAlertRules', data)
export const roundCreateRelatedRoundStops = <T>(data?: any) =>
  handlePostRequest<T>('Round/CreateRelatedRoundStops', data)
export const getTenantRequestsSummary = <T>(data?: any) => handleGetRequest<T>('Ticket/GetTenantRequestsSummary', data)
export const getTenantRequestTypeSummary = <T>(data?: any) =>
  handleGetRequest<T>('Ticket/GetTenantRequestTypeSummary', data)
export const getTenantRequestMonthSummary = <T>(data?: any) =>
  handleGetRequest<T>('Ticket/GetTenantRequestMonthSummary', data)
export const getRequestTypeMonthSummary = <T>(data?: any) =>
  handleGetRequest<T>('Ticket/GetRequestTypeMonthSummary', data)
export const getTenantServiceRequestSummary = <T>(data?: any) =>
  handleGetRequest<T>('Ticket/GetTenantServiceRequestSummary', data)
export const getCustomerFeedbackAISummary = <T>(data?: any) =>
  handleGetRequest<T>('Feedback/GetCustomerFeedbackAISummary', data)
export const getTenantCustomerFeedbackScoreSummary = <T>(data?: any) =>
  handleGetRequest<T>('Feedback/GetTenantCustomerFeedbackScoreSummary', data)
export const saveCollectionQuestions = <T>(data?: any) =>
  handlePostRequest<T>('Inspection/SaveCollectionQuestions', data)
export const reviewInspectionReport = <T>(data?: any) => handlePostRequest<T>('Inspection/ReviewInspectionReport', data)
export const sendReminder = <T>(data?: any) => handlePostRequest<T>('Inspection/SendReminder', data)
export const getInspectionSubmitDetail = <T>(data?: any) =>
  handleGetRequest<T>('Inspection/GetInspectionSubmitDetail', data)
export const getCollectionSubmissions = <T>(data?: any) =>
  handleGetRequest<T>('Inspection/GetCollectionSubmissions', data)
export const getTenantResponsibilities = <T>(data?: any) =>
  handleGetRequest<T>('Inspection/GetTenantResponsibilities', data)
export const startAndSubmitCollection = <T>(data?: any) => handlePostRequest<T>('Inspection/StartAndSubmit', data)
export const getContactGroups = <T>(data?: any) => handleGetRequest<T>('Announcement/GetContactGroups', data)
export const getEmailTemplates = <T>(data?: any) => handleGetRequest<T>('Announcement/GetEmailTemplates', data)
export const saveContactGroup = <T>(data?: any) => handlePostRequest<T>('Announcement/SaveContactGroup', data)
export const sendAnnouncementEmail = <T>(data?: any) => handlePostRequest<T>('Announcement/SendAnnouncementEmail', data)
export const addRelatedRoundStops = <T>(data?: any) => handlePostRequest<T>('Round/AddRelatedRoundStops', data)
export const workOrderRelatedRoundStops = <T>(data?: any) =>
  handlePostRequest<T>('WorkOrder/AddRelatedRoundStops', data)

// Library
export const getDocuments = <T>(data?: any) => handleGetRequest<T>('Library/GetDocuments', data)
export const saveDocument = <T>(data?: any) => handlePostRequest<T>('Library/SaveDocument', data)
export const updateDocument = <T>(data?: any) => handlePostRequest<T>('Library/UpdateDocument', data)
export const deleteDocument = <T>(data?: any) => handlePostRequest<T>('Library/DeleteDocument', data)
export const attachInspectionDocument = <T>(data?: any) => handlePostRequest<T>('Round/AttachLibraryDocument', data)
export const detachInspectionDocument = <T>(data?: any) => handlePostRequest<T>('Round/DetachLibraryDocument', data)
// Future scope — Template Library
// export const getLibraryTemplates = <T>(data?: any) => handleGetRequest<T>('Library/GetTemplates', data)
// export const saveLibraryTemplate = <T>(data?: any) => handlePostRequest<T>('Library/SaveTemplate', data)
// export const deleteLibraryTemplate = <T>(data?: any) => handlePostRequest<T>('Library/DeleteTemplate', data)

// ── Impersonation ────────────────────────────────────────────────────────────
export const searchImpersonationUsers = <T>(data: { st: string }) =>
  handleGetRequest<T>('Impersonation/SearchUsers', data)
export const getImpersonationUserContext = <T>(data: { acId: string }) =>
  handlePostRequest<T>('Impersonation/GetUserContext', data)
export const searchImpersonationDestinations = <T>(data: { acId: string; st: string }) =>
  handlePostRequest<T>('Impersonation/SearchDestinations', data)
export const createImpersonation = <T>(data: { acId: string; qrIds: string[] }) =>
  handlePostRequest<T>('Impersonation/Create', data)
export const redeemImpersonation = <T>(data: { code: string }) =>
  handlePostRequest<T>('ImpersonationRedeem/Redeem', data)

// ========== Roles & Feature Flags (AcAdmin dashboard) ==========
export const listRoles = <T>(data: { IncludeArchived?: boolean } = {}) => handlePostRequest<T>('Role/ListRoles', data)
export const createRole = <T>(data: any) => handlePostRequest<T>('Role/CreateRole', data)
export const updateRole = <T>(data: any) => handlePostRequest<T>('Role/UpdateRole', data)
export const archiveRole = <T>(data: { Name: string }) => handlePostRequest<T>('Role/ArchiveRole', data)

export const listFeatures = <T>(data?: { GroupId?: string }) => handlePostRequest<T>('Feature/ListFeatures', data || {})
export const toggleGlobalFeature = <T>(data: { FeatureName: string; GlobalEnabled: boolean }) =>
  handlePostRequest<T>('Feature/ToggleGlobal', data)
export const updateGroupFeatureFlags = <T>(data: {
  GroupId: string
  FeatureFlags: { FeatureName: string; FeatureEnabled: boolean }[]
}) => handlePostRequest<T>('Feature/UpdateGroupFlags', data)
export const getPermissionsByFeature = <T>() => handlePostRequest<T>('Feature/GetPermissionsByFeature', {})

export const listSystemSettings = <T>() => handlePostRequest<T>('SystemSetting/ListSettings', {})
export const updateSystemSetting = <T>(data: { SettingKey: string; SettingValue: string }) =>
  handlePostRequest<T>('SystemSetting/UpdateSetting', data)

// ========== Mapping Hub (specs#3281) ==========
// gId/bId ride on every call — the backend buildingContext reads them from
// query (GET) or body (POST).
export const getMappingHub = <T>(data: { gId: string; bId: string }) =>
  handleGetRequest<T>('MappingHub/GetMappingHub', data)
export const getMapCategories = <T>(data: { gId: string; bId: string }) =>
  handleGetRequest<T>('MappingHub/GetMapCategories', data)
export const getMapItems = <T>(data: { gId: string; bId?: string; categoryId?: string }) =>
  handleGetRequest<T>('MappingHub/GetMapItems', data)
export const getMapItem = <T>(data: { id: string }) => handleGetRequest<T>('MappingHub/GetMapItem', data)
export const saveMapItem = <T>(data: any) => handlePostRequest<T>('MappingHub/SaveMapItem', data)
export const archiveMapItem = <T>(data: { MapItemId: string }) =>
  handlePostRequest<T>('MappingHub/ArchiveMapItem', data)
export const saveMapCategory = <T>(data: any) => handlePostRequest<T>('MappingHub/SaveMapCategory', data)
export const archiveMapCategory = <T>(data: { CategoryId: string }) =>
  handlePostRequest<T>('MappingHub/ArchiveMapCategory', data)
export const saveMapCategorySetting = <T>(data: {
  gId: string
  bId: string
  CategoryId: string
  Hidden?: boolean
  SortOrder?: number
}) => handlePostRequest<T>('MappingHub/SaveMapCategorySetting', data)
export const scanNameplate = <T>(data: { MapItemId?: string; Base64?: string }) =>
  handlePostRequest<T>('MappingHub/ScanNameplate', data)
export const convertMapItemToEquipment = <T>(data: { MapItemId: string }) =>
  handlePostRequest<T>('MappingHub/ConvertToEquipment', data)
export const saveMapHubSetting = <T>(data: { gId: string; bId: string; HeaderImageUrl: string }) =>
  handlePostRequest<T>('MappingHub/SaveMapHubSetting', data)
// The PIN a building's Mapping Hub QR accepts when opened directly (specs#3492).
export const getMapHubPin = <T>(data: { gId: string; bId: string }) => handleGetRequest<T>('MappingHub/GetMapHubPin', data)
export const saveMapHubPin = <T>(data: { gId: string; bId: string; Code: string }) =>
  handlePostRequest<T>('MappingHub/SaveMapHubPin', data)

// ── PCA Hub (specs#3296) ─────────────────────────────────────────────────────
// The report template is per portfolio per property type. gId rides on every
// call: an AcAdmin's session group does not follow the account switcher.
export const pcaGetTemplate = <T>(data: { gId: string; propertyType: string }) =>
  handleGetRequest<T>('PcaHub/GetTemplate', data)
export const pcaGetDefaultTemplate = <T>(data: { gId: string; propertyType: string }) =>
  handleGetRequest<T>('PcaHub/GetDefaultTemplate', data)
export const pcaSaveTemplate = <T>(data: {
  gId: string
  PropertyType: string
  Sections: any[]
  Types: any[]
  ModifiedDate: number | null
}) => handlePostRequest<T>('PcaHub/SaveTemplate', data)
// PCA report: every inspection in the portfolio, one inspection with its items,
// and the report-only details (field answers, document review, free text).
export const pcaGetReportList = <T>(data: { gId: string }) => handleGetRequest<T>('PcaHub/GetReportList', data)
export const pcaGetInspection = <T>(data: { gId: string; id: string }) =>
  handleGetRequest<T>('PcaHub/GetInspection', data)
export const pcaSaveReport = <T>(data: {
  gId: string
  InspectionId: string
  Answers?: Record<string, string>
  Documents?: Record<string, any>
  ExecutiveSummary?: string
  Limitations?: string
}) => handlePostRequest<T>('PcaHub/SaveReport', data)
export const pcaSaveStrategy = <T>(data: { gId: string; InspectionId: string; Strategy: Record<string, any> }) =>
  handlePostRequest<T>('PcaHub/SaveStrategy', data)

// ---- Survey 2.0 (Collect) ----
export const collectGetTemplates = <T>() => handleGetRequest<T>('Collect/GetTemplates')
export const collectSaveTemplate = <T>(data: { TemplateId?: string; Name: string; Categories: any[] }) =>
  handlePostRequest<T>('Collect/SaveTemplate', data)
export const collectArchiveTemplate = <T>(data: { TemplateId: string }) =>
  handlePostRequest<T>('Collect/ArchiveTemplate', data)
export const collectSendSurvey = <T>(data: {
  GroupId: string
  BuildingIds: string[]
  TemplateId: string
  Name: string
  Instructions?: string
  Recipients: { Email: string; Name?: string; Role: string }[]
  Bcc?: string
  Pin?: string
  ExpiryDays?: number
}) => handlePostRequest<T>('Collect/SendSurvey', data)
export const collectResendSurvey = <T>(data: { SurveyId: string; Email: string }) =>
  handlePostRequest<T>('Collect/ResendSurvey', data)
export const collectCloseSurvey = <T>(data: { SurveyId: string }) => handlePostRequest<T>('Collect/CloseSurvey', data)
export const collectReopenSurvey = <T>(data: { SurveyId: string }) => handlePostRequest<T>('Collect/ReopenSurvey', data)
export const collectUpdateBuildingExpiry = <T>(data: {
  SurveyId: string
  BuildingId: string
  ExpireDate?: string
  ExpiryDays?: number
}) => handlePostRequest<T>('Collect/UpdateBuildingExpiry', data)
export const collectArchiveSurvey = <T>(data: { SurveyId: string }) =>
  handlePostRequest<T>('Collect/ArchiveSurvey', data)
export const collectDeleteSurvey = <T>(data: { SurveyId: string }) =>
  handlePostRequest<T>('Collect/DeleteSurvey', data)
export const collectGetDashboard = <T>() => handleGetRequest<T>('Collect/GetDashboard')
export const collectGetSurveyDetail = <T>(data: { surveyId: string }) =>
  handleGetRequest<T>('Collect/GetSurveyDetail', data)
export const collectGetEmailLibrary = <T>() => handleGetRequest<T>('Collect/GetEmailLibrary')
export const collectGetGroupsAndBuildings = <T>() => handleGetRequest<T>('Collect/GetGroupsAndBuildings')
export const collectGetRecipientOptions = <T>(data: { gId: string; bIds: string }) =>
  handleGetRequest<T>('Collect/GetRecipientOptions', data)
export const collectAdminSaveResponse = <T>(data: {
  SurveyId: string
  BuildingId: string
  ItemsPatch?: any
  AnswersPatch?: any
  SectionNotesPatch?: any
}) => handlePostRequest<T>('Collect/AdminSaveResponse', data)
export const pubCollectGetSurvey = <T>(data: { token: string; pin?: string }) =>
  handleGetRequest<T>('PubCollect/GetSurveyByToken', data)
export const pubCollectSaveResponse = <T>(data: {
  Token: string
  Pin?: string
  BuildingId: string
  ItemsPatch?: any
  AnswersPatch?: any
  SectionNotesPatch?: any
  TenantRespPatch?: any
  TenantRows?: string[]
}) => handlePostRequest<T>('PubCollect/SaveResponse', data)
// Same save, for the page-unload flush: a keepalive fetch outlives the document, an
// axios call made from beforeunload is cancelled with it. PubCollect is anonymous
// (token-scoped), so no auth header is needed. Fire-and-forget by design.
export const pubCollectSaveResponseKeepalive = (data: Parameters<typeof pubCollectSaveResponse>[0]) => {
  try {
    return fetch(`${CONFIG.apiHost}/csm/PubCollect/SaveResponse`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).catch(() => undefined)
  } catch {
    return Promise.resolve(undefined)
  }
}
export const pubCollectUploadFile = <T>(data: {
  Token: string
  Pin?: string
  BuildingId: string
  ItemKey: string
  FileName: string
  FileType: string
  Base64: string
}) => handlePostRequest<T>('PubCollect/UploadCollectFile', data)
export const pubCollectSubmitSurvey = <T>(data: { Token: string; Pin?: string }) =>
  handlePostRequest<T>('PubCollect/SubmitSurvey', data)
export const collectRunImports = <T>(data: { SurveyId: string }) => handlePostRequest<T>('Collect/RunImports', data)
export const pubCollectGetContacts = <T>(data: { token: string; pin?: string; buildingId: string }) =>
  handleGetRequest<T>('PubCollect/GetContacts', data)
export const pubCollectAssignSections = <T>(data: {
  Token: string
  Pin?: string
  BuildingId: string
  Categories: string[]
  Role?: string
  Email: string
  Name?: string
  Note?: string
}) => handlePostRequest<T>('PubCollect/AssignSections', data)
export const pubCollectRevokeAssignment = <T>(data: {
  Token: string
  Pin?: string
  BuildingId: string
  Category: string
}) => handlePostRequest<T>('PubCollect/RevokeAssignment', data)
export const collectReviewIngestionRun = <T>(data: {
  RunId: string
  Action: 'Approve' | 'Skip' | 'Reclassify' | 'Rescan'
  Kind?: string
  ReferenceId?: string
  ReferenceKind?: string
  CreateNew?: { Name: string; Kind?: string; Unit?: string; Floor?: string }
  Note?: string
}) => handlePostRequest<T>('Collect/ReviewIngestionRun', data)
export const collectGetLinkCandidates = <T>(data: { surveyId: string; buildingId: string }) =>
  handleGetRequest<T>('Collect/GetLinkCandidates', data)
export const collectGetIngestionRuns = <T>(data: { surveyId: string }) =>
  handleGetRequest<T>('Collect/GetIngestionRuns', data)
// ---- Compliance Dashboard ----
export const complianceGetScope = <T>(data: { gId: string }) => handleGetRequest<T>('Compliance/GetScope', data)
export const complianceGetDashboard = <T>(data: { gId: string; bId: string; yearsBack?: number }) =>
  handleGetRequest<T>('Compliance/GetDashboard', data)
// tenants: 1 asks for the Setup matrix's shape — one section per tenant. Without
// it the answer is the flat, tenant-agnostic list the Collections tab edits.
export const complianceGetRequirements = <T>(data: { gId: string; bId: string; tenants?: 1 }) =>
  handleGetRequest<T>('Compliance/GetRequirements', data)
// A row with no TenantId is written to EVERY tenant of the building (that is how
// the Collections tab saves one cadence for the whole building); the Setup matrix
// names a tenant on every row and asks for the per-tenant answer back.
export const complianceSaveRequirements = <T>(data: {
  GroupId: string
  BuildingId: string
  Requirements: any[]
  ArchiveMissingCustom?: boolean
  WithTenants?: boolean
}) => handlePostRequest<T>('Compliance/SaveRequirements', data)
export const complianceSaveRecord = <T>(data: {
  GroupId: string
  BuildingId: string
  TenantId?: string
  ItemKey: string
  Year: number
  SubmittedDate?: string
  Note?: string
}) => handlePostRequest<T>('Compliance/SaveRecord', data)
export const complianceUploadRecordFile = <T>(data: {
  GroupId: string
  BuildingId: string
  ItemKey: string
  Year: number
  FileName: string
  FileType: string
  Base64: string
  SubmittedDate?: string
  Note?: string
  TenantId?: string
  ExpirationDate?: string
}) => handlePostRequest<T>('Compliance/UploadRecordFile', data)
export const complianceRemoveRecordFile = <T>(data: { RecordId: string; Link: string }) =>
  handlePostRequest<T>('Compliance/RemoveRecordFile', data)
export const complianceSendEmail = <T>(data: {
  GroupId: string
  BuildingId: string
  To: string
  Bcc?: string
  Subject: string
  Body: string
  Link?: string
}) => handlePostRequest<T>('Compliance/SendEmail', data)
export const complianceGetRecipientOptions = <T>(data: { gId: string; bId: string }) =>
  handleGetRequest<T>('Compliance/GetRecipientOptions', data)
export const complianceGetPortfolio = <T>(data: { gId: string; year?: number }) =>
  handleGetRequest<T>('Compliance/GetPortfolio', data)
